import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Table,
  Button,
  Dropdown,
  Menu,
  Input,
  InputNumber,
  Row,
  Col,
  Checkbox,
  Tag,
  Typography,
  Card,
  Avatar,
  Tooltip,
  Popconfirm,
  Modal,
  Form,
  Select,
  DatePicker,
  Drawer,
  Space as AntSpace,
} from 'antd';
import { SearchOutlined, FileExcelOutlined } from '@ant-design/icons';
import ProposalService from '../../services/ProposalService';
import {
  capitalizeWords,
  formatDate,
  formatDateString,
  formatToTL,
  hasRole,
  normalizeTurkish,
  openNotification,
  extractApiErrorMessage,
  COMPANY_INFO_WARNING_MESSAGE,
  isAccountActive,
} from '../../utils';
import CompanyService from '../../services/CompanyService';
import StatusService from '../../modules/Status/Service/StatusService';
import tinycolor from 'tinycolor2';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import PlanningModal from '../Companies/PlanningModal';
import AuthorizedPersonTable from '../Companies/AuthorizedPersonTable';
import DocumentUploadModal from '../Companies/UploadModal';
import ApproveDocument from '../Companies/ApproveDocument';
import ReportInvoiceModal from './ReportInvoiceModal';
import PaymentModal from './PaymentModal';
import DiscountModal from '../Proposal/DiscountModal';
import PdfUpload from '../Proposal/PdfUpload';
import dayjs from 'dayjs';
import locale from 'antd/es/date-picker/locale/tr_TR';
import 'dayjs/locale/tr';
import ProposalView from '../Proposal/ProposalView';
import NegativeProposalReasonModal from '../Proposal/NegativeProposalReasonModal';
import AttributeService from '../../services/AttributeService';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import UserService from '../../services/UserService';
import AuthorizedPersonService from '../../services/AuthorizedPersonService';
import { debounce } from 'lodash';
import { UserListIcon } from 'hugeicons-react';
import CategoryListDrawer from './CategoryListDrawer';
import ReportApprovalDrawer from './ReportApprovalDrawer';
import type { NotificationType } from '../../constants/notificationTypes';
import AssigneeSummary from '../Proposal/AssigneeSummary';
import SettingService, { SettingItem } from '../../services/SettingService';
import {
  getMatchingAuthorizedEmailOptions,
  hasMatchingAuthorizedRecipients,
} from '../../utils/proposalAuthorizedRecipients';
import RenewalSettingsDrawer, { RenewalSettingsFormValues } from '../Proposal/RenewalSettingsDrawer';
import ProposalProcessHistoryDrawer from '../Proposal/ProposalProcessHistoryDrawer';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
  buildOpportunityProposalCreateLink,
  isControlCompletedProposal,
} from '../Opportunity/opportunityLinks';
const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const PDF_ARCHIVE_REFRESH_WARNING_MESSAGE =
  'Teklif tutarı güncellendi ancak teklif PDF’i yeniden oluşturulamadı. Lütfen tekrar deneyin.';
const DISCOUNT_PDF_REFRESH_DELAY_MS = 3000;
const SUMMARY_TOTAL_TYPES = [1, 2, 3, 7];
const EMPTY_SUMMARY_TOTALS = {
  totalPrice: 0,
  machineMinutes: 0,
  electricMinutes: 0,
  hygieneMinutes: 0,
};

const ASSIGNEE_SETTING_KEYS = [
  'PROPOSAL_DEFAULT_OFFER_ASSIGNE_USER_ID',
  'PROPOSAL_DEFAULT_PLAN_ASSIGNE_USER_ID',
  'PROPOSAL_DEFAULT_LEGAL_ASSIGNE_USER_ID',
  'PROPOSAL_DEFAULT_REPORT_ASSIGNE_USER_ID',
  'PROPOSAL_DEFAULT_INVOICE_ASSIGNE_USER_ID',
  'PROPOSAL_DEFAULT_FINANCE_ASSIGNE_USER_ID',
] as const;

type AssigneeFormValues = {
  offerAssigneeUserId?: string;
  planAssigneeUserId?: string;
  legalAssigneeUserId?: string;
  reportAssigneeUserId?: string;
  invoiceAssigneeUserId?: string;
  financeAssigneeUserId?: string;
};

type NegativeReasonFormValues = {
  negativeReasonId?: number;
  negativeReasonText?: string;
};

type ManualEmailType = 'PLAN' | 'REPORT' | 'FINANCIAL';

type ManualEmailFormValues = {
  emails?: string[];
};

const ASSIGNEE_SETTING_FIELD_MAP: Record<typeof ASSIGNEE_SETTING_KEYS[number], keyof AssigneeFormValues> = {
  PROPOSAL_DEFAULT_OFFER_ASSIGNE_USER_ID: 'offerAssigneeUserId',
  PROPOSAL_DEFAULT_PLAN_ASSIGNE_USER_ID: 'planAssigneeUserId',
  PROPOSAL_DEFAULT_LEGAL_ASSIGNE_USER_ID: 'legalAssigneeUserId',
  PROPOSAL_DEFAULT_REPORT_ASSIGNE_USER_ID: 'reportAssigneeUserId',
  PROPOSAL_DEFAULT_INVOICE_ASSIGNE_USER_ID: 'invoiceAssigneeUserId',
  PROPOSAL_DEFAULT_FINANCE_ASSIGNE_USER_ID: 'financeAssigneeUserId',
};

const ASSIGNEE_SETTING_TITLE_MAP: Record<string, string> = {
  PROPOSAL_DEFAULT_OFFER_ASSIGNE_USER_ID: 'Teklif Sorumlusu',
  PROPOSAL_DEFAULT_PLAN_ASSIGNE_USER_ID: 'Plan Sorumlusu',
  PROPOSAL_DEFAULT_LEGAL_ASSIGNE_USER_ID: 'Yasal Bildirim Sorumlusu',
  PROPOSAL_DEFAULT_REPORT_ASSIGNE_USER_ID: 'Rapor Sorumlusu',
  PROPOSAL_DEFAULT_INVOICE_ASSIGNE_USER_ID: 'Fatura Sorumlusu',
  PROPOSAL_DEFAULT_FINANCE_ASSIGNE_USER_ID: 'Finans Sorumlusu',
};

interface ProposalListWithDetailsProps {
  defaultStatusIds: number[];
  type: number;
  paymentType?: number | null;
  sortFieldProp?: string;
  sortDirectionProp?: 'ASC' | 'DESC';
  variant?: 'default' | 'compact';
  initialPageSize?: number;
  categoryDrawerReadOnly?: boolean;
}

const ProposalListWithDetails: React.FC<ProposalListWithDetailsProps> = ({
  defaultStatusIds,
  type,
  paymentType = null,
  sortFieldProp = 'createdAt',
  sortDirectionProp = 'ASC',
  variant = 'default',
  initialPageSize = 50,
  categoryDrawerReadOnly = false,
}) => {

  const [filteredProposals, setFilteredProposals] = useState<any[]>([]);
  const [searchText, setSearchText] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [searchParams] = useSearchParams();
  const searchParamsString = searchParams.toString();
  const queryParamValue = useMemo(
    () => (searchParams.get('search') ?? '').trim(),
    [searchParamsString],
  );
  const [hasInitializedQueryParam, setHasInitializedQueryParam] = useState(false);
  const [statuses, setStatuses] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1); // Başlangıç sayfası 1
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const logoCache = useRef<Map<number, string>>(new Map());
  const pendingRequests = useRef<Map<number, Promise<string | null>>>(new Map());
  const [isPlanningModalVisible, setIsPlanningModalVisible] = useState(false);
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
  const [isDocumentModalVisible, setIsDocumentModalVisible] = useState(false);
  const [negativeModalVisible, setNegativeModalVisible] = useState(false);
  const [negativeModalLoading, setNegativeModalLoading] = useState(false);
  const [negativeModalProposal, setNegativeModalProposal] = useState<any | null>(null);
  const [isStatusChange, setIsStatusChange] = useState(false);
  const [selectedProposalId, setSelectedProposalId] = useState<number | null>(
    null
  );
  const [sortField, setSortField] = useState(sortFieldProp);
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>(sortDirectionProp);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isCentralUser, setIsCentralUser] = useState(false);
  const isCustomer = useMemo(() => hasRole('CUSTOMER'), []);
  const [onlyMyCompanies, setOnlyMyCompanies] = useState<boolean>(false);
  const cityIdsToShow = [2170, 2195, 2150, 2167, 2151, 2176, 2163, 2218, 2175];

  // permissions ekliyoruz
  const [permissions, setPermissions] = useState<string[]>([]);
  const [isCreate, setIsCreate] = useState(false);

  const [selectedPaidStatus, setPaidSelectedStatus] = useState<any>();
  const userId = localStorage.getItem('id'); // user ID
  const [isInvoiceModalVisible, setIsInvoiceModalVisible] = useState(false);
  const [isPaymentModalVisible, setIsPaymentModalVisible] = useState(false);
  const [paymentModalType, setPaymentModalType] = useState<any>();
  const [isCategoryDrawerVisible, setIsCategoryDrawerVisible] = useState(false);
  const [categoryDrawerProposalId, setCategoryDrawerProposalId] = useState<number | null>(null);
  const [isReportApprovalDrawerVisible, setIsReportApprovalDrawerVisible] = useState(false);
  const [reportApprovalProposalId, setReportApprovalProposalId] = useState<number | null>(null);
  const [reportApprovalProposalNumber, setReportApprovalProposalNumber] = useState<string | null>(null);
  const [isNotesDrawerOpen, setIsNotesDrawerOpen] = useState(false);
  const [noteTargetProposal, setNoteTargetProposal] = useState<any | null>(null);
  const [invoiceModalData, setInvoiceModalData] = useState<any>();
  const [invoiceModalType, setInvoiceModalType] = useState<any>();
  const [attributes, setAttributes] = useState<any>();
  const [selectedProposal, setSelectedProposal] = useState<any | null>(null);
  const [isDiscountModalVisible, setIsDiscountModalVisible] = useState(false);
  const [documentModalType, setDocumentModalType] = useState(1);
  const [currentUserMaxDiscountRate, setCurrentUserMaxDiscountRate] = useState<number | null>(null);
  const [isDiscountSubmitLoading, setIsDiscountSubmitLoading] = useState(false);
  const [discountPdfArchiveProposalId, setDiscountPdfArchiveProposalId] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [summaryLoading, setSummaryLoading] = useState<boolean>(false);
  const [summaryTotals, setSummaryTotals] = useState(EMPTY_SUMMARY_TOTALS);
  const summaryRequestId = useRef(0);
  const [exporting, setExporting] = useState<boolean>(false);
  const [open, setOpen] = useState(false);
  const isCompact = variant === 'compact';
  const [filter, setFilter] = useState(0);
  const shouldShowSummaryTotals = SUMMARY_TOTAL_TYPES.includes(type);
  const canViewEffortSummaryTotals = isAdmin || isCentralUser;

  // Yetkili kişiler drawer state
  const [isAuthorizedDrawerVisible, setIsAuthorizedDrawerVisible] = useState(false);
  const [authorizedCompanyId, setAuthorizedCompanyId] = useState<number | null>(null);
  const [authorizedProposalId, setAuthorizedProposalId] = useState<number | null>(null);
  const [selectedAuthorizedIds, setSelectedAuthorizedIds] = useState<number[]>([]);

  const [companyList, setCompanyList] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);

  const [districts, setDistricts] = useState<{ id: number; name: string }[]>([]);
  const [cities, setCities] = useState<{ id: number, name: string }[]>([]);

  const [selectedCityId, setSelectedCityId] = useState<number | null>(null);
  const [selectedDistrictId, setSelectedDistrictId] = useState<number | null>(null);
  const [assigneeModalVisible, setAssigneeModalVisible] = useState(false);
  const [assigneeModalLoading, setAssigneeModalLoading] = useState(false);
  const [assigneeUpdateLoading, setAssigneeUpdateLoading] = useState(false);
  const [assigneeSettings, setAssigneeSettings] = useState<SettingItem[]>([]);
  const [assigneeTargetProposal, setAssigneeTargetProposal] = useState<any | null>(null);
  const [assignableUsers, setAssignableUsers] = useState<any[]>([]);
  const [assignableUsersAll, setAssignableUsersAll] = useState<any[]>([]);
  const [assigneeForm] = Form.useForm<AssigneeFormValues>();
  const [isDueDayModalVisible, setIsDueDayModalVisible] = useState(false);
  const [dueDayTargetProposal, setDueDayTargetProposal] = useState<any | null>(null);
  const [dueDayUpdateLoading, setDueDayUpdateLoading] = useState(false);
  const [dueDayForm] = Form.useForm<{ dueDay?: number }>();
  const [renewalSettingsOpen, setRenewalSettingsOpen] = useState(false);
  const [renewalSettingsTargetProposal, setRenewalSettingsTargetProposal] = useState<any | null>(null);
  const [renewalSettingsLoading, setRenewalSettingsLoading] = useState(false);
  const [manualEmailModalVisible, setManualEmailModalVisible] = useState(false);
  const [manualEmailModalLoading, setManualEmailModalLoading] = useState(false);
  const [manualEmailTargetProposal, setManualEmailTargetProposal] = useState<any | null>(null);
  const [manualEmailType, setManualEmailType] = useState<ManualEmailType | null>(null);
  const [manualEmailForm] = Form.useForm<ManualEmailFormValues>();
  const handleOpenNotesDrawer = (record: any) => {
    setNoteTargetProposal(record);
    setIsNotesDrawerOpen(true);
  };

  const handleCloseNotesDrawer = () => {
    setIsNotesDrawerOpen(false);
    setNoteTargetProposal(null);
  };
  const filterClear = () => {
    setDateRange([null, null]);
    setInvoiceDateRange([null, null]);
    setPaidDateRange([null, null]);
    setPlanningPaidDateRange([null, null]);
    setApprovedPlanningDateRange([null, null]);
    setRequestedPlanningDateRange([null, null]);
    setDocumentUploadedAtRange([null, null]);
    setOnlyMyCompanies(false);
    setSelectedCompanyId(null);
    setSelectedCustomerId(null);
    setSelectedCityId(null);
    setSelectedDistrictId(null);
    setCurrentPage(1);
    setFilter(filter + 1);
  };

  const getCompanyAll = () => {
    CompanyService.getCompanyAll()
      .then((response: any) => {
        setCompanyList(response.data);
      })
      .catch((error) => console.error('Statüler yüklenirken hata oluştu:', error));
  }

  const getCities = () => {
    CompanyService.getStates(225)
      .then((response: any) => {
        setCities(response.data);
      })
  }

  const getFilterProposal = () => {
    setCurrentPage(1);
    setFilter(filter + 1);
    setOpen(false);
  };

  const [customerList, setCustomerList] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);

  const onDateRangeChange = (dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
    if (dates) {
      setDateRange([
        dates[0]?.format('YYYY-MM-DD') ?? null,
        dates[1]?.format('YYYY-MM-DD') ?? null
      ]);
    } else {
      setDateRange([null, null]);
    }
  };

  const onInvoiceDateRangeChange = (dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
    if (dates) {
      setInvoiceDateRange([
        dates[0]?.format('YYYY-MM-DD') ?? null,
        dates[1]?.format('YYYY-MM-DD') ?? null
      ]);
    } else {
      setInvoiceDateRange([null, null]);
    }
  };

  const onPaidDateRangeChange = (dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
    if (dates) {
      setPaidDateRange([
        dates[0]?.format('YYYY-MM-DD') ?? null,
        dates[1]?.format('YYYY-MM-DD') ?? null
      ]);
    } else {
      setPaidDateRange([null, null]);
    }
  };

  const onPlanningPaidDateRangeChange = (dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
    if (dates) {
      setPlanningPaidDateRange([
        dates[0]?.format('YYYY-MM-DD') ?? null,
        dates[1]?.format('YYYY-MM-DD') ?? null
      ]);
    } else {
      setPlanningPaidDateRange([null, null]);
    }
  };

  const onApprovedPlanningDateRangeChange = (dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
    if (dates) {
      setApprovedPlanningDateRange([
        dates[0]?.format('YYYY-MM-DD') ?? null,
        dates[1]?.format('YYYY-MM-DD') ?? null
      ]);
    } else {
      setApprovedPlanningDateRange([null, null]);
    }
  };

  const onRequestedPlanningDateRangeChange = (dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
    if (dates) {
      setRequestedPlanningDateRange([
        dates[0]?.format('YYYY-MM-DD') ?? null,
        dates[1]?.format('YYYY-MM-DD') ?? null
      ]);
    } else {
      setRequestedPlanningDateRange([null, null]);
    }
  };

  const onDocumentUploadedAtRangeChange = (dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
    if (dates) {
      setDocumentUploadedAtRange([
        dates[0]?.format('YYYY-MM-DD') ?? null,
        dates[1]?.format('YYYY-MM-DD') ?? null
      ]);
    } else {
      setDocumentUploadedAtRange([null, null]);
    }
  };


  const getDistricts = async (stateId: number) => {
    try {
      const response = await CompanyService.getDistricts(stateId);
      setDistricts(response.data);
    } catch (error) {
      console.error("İlçeler alınırken hata oluştu:", error);
    }
  };

  const handleDistrictChange = (value: number) => {
    setSelectedDistrictId(value);
  };

  const handleCityChange = (value: number) => {
    const city = cities.find((c) => c.id === value);
    setSelectedCityId(value);
    setSelectedDistrictId(null);
    setDistricts([]);
    if (value) {
      getDistricts(value);
    }
  };

  const handleCustomerChange = (value: number) => {
    setSelectedCustomerId(value);
  };

  const handleCompanyChange = (value: number) => {
    setSelectedCompanyId(value);
  };

  const reorderAssigneeSettings = (items: SettingItem[]): SettingItem[] => {
    const orderMap = new Map(ASSIGNEE_SETTING_KEYS.map((key, index) => [key, index]));
    return items
      .filter((item) => item?.key && orderMap.has(item.key as typeof ASSIGNEE_SETTING_KEYS[number]))
      .sort(
        (a, b) =>
          (orderMap.get(a.key as typeof ASSIGNEE_SETTING_KEYS[number]) ?? 99) -
          (orderMap.get(b.key as typeof ASSIGNEE_SETTING_KEYS[number]) ?? 99),
      );
  };

  const buildAssigneeInitialValues = (proposal: any, settings: SettingItem[]): AssigneeFormValues => {
    const values: AssigneeFormValues = {};
    const settingMap = new Map(settings.map((item) => [item.key, item.value]));
    ASSIGNEE_SETTING_KEYS.forEach((key) => {
      const fieldName = ASSIGNEE_SETTING_FIELD_MAP[key];
      const proposalValue = proposal ? proposal[fieldName] : null;
      const settingValue = settingMap.get(key);
      const resolved = proposalValue != null ? String(proposalValue) : settingValue || undefined;
      (values as any)[fieldName] = resolved || undefined;
    });
    return values;
  };

  const loadAssignableUsers = useCallback(async () => {
    try {
      const resp: any = await UserService.getUsersWithoutPagination({});
      const list: any[] = resp?.data?.content ?? resp?.data ?? [];
      const roleFiltered = list.filter((u) => {
        const roles: any[] = u?.roles ?? [];
        const roleNames = roles.map((r: any) => r?.name).filter(Boolean);
        return !roleNames.includes('INSPECTOR') && !roleNames.includes('CUSTOMER');
      });
      const activeOnly = roleFiltered.filter((u) => isAccountActive(u));
      setAssignableUsersAll(roleFiltered);
      setAssignableUsers(activeOnly);
      return activeOnly;
    } catch (error) {
      console.error('Assignable users could not be loaded', error);
      return [];
    }
  }, []);

  const onClose = () => {
    setOpen(false);
  };

  const showDrawer = () => {
    setOpen(true);
  };

  const applyDiscount = (record: any) => {
    setSelectedProposal(record);
    setIsDiscountModalVisible(true);
  };

  const handleModalCancel = () => {
    if (isDiscountSubmitLoading) return;
    setIsDiscountModalVisible(false);
    setIsUploadModalVisible(false);
    setIsPlanningModalVisible(false);
  };

  //   setSelectedProposal(record);
  // setIsDiscountModalVisible(true);

  const navigate = useNavigate();


  const getCustomerAll = () => {
    UserService.getAllCustomer()
      .then((response: any) => {
        setCustomerList(response.data);
      })
      .catch((error) => console.error('Statüler yüklenirken hata oluştu:', error));
  }

  useEffect(() => {

    AttributeService.getAll()
      .then((response) => {
        setAttributes(response.data);
      })
      .catch((error) => {
        console.error('Error fetching attributes:', error);
      });

    const isUserAdmin = hasRole('ADMIN');
    setIsAdmin(isUserAdmin);

    const isUserCentralUser = hasRole('CENTRAL_USER');
    setIsCentralUser(isUserCentralUser);

    getCities();

    if (isUserAdmin || isUserCentralUser) {
      getCustomerAll();
      getCompanyAll();
    }

  }, [])

  useEffect(() => {
    let isMounted = true;

    const fetchMaxDiscountRate = async () => {
      if (hasRole('ADMIN')) {
        if (isMounted) {
          setCurrentUserMaxDiscountRate(null);
        }
        return;
      }

      try {
        const response = await UserService.getMe();
        if (!isMounted) return;
        const rate = response?.data?.maxDiscountRate;
        setCurrentUserMaxDiscountRate(
          typeof rate === 'number' && !Number.isNaN(rate) ? rate : null
        );
      } catch (error) {
        if (isMounted) {
          console.error('Kullanıcı indirim oranı alınamadı:', error);
          setCurrentUserMaxDiscountRate(null);
        }
      }
    };

    fetchMaxDiscountRate();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (isAdmin) {
      loadAssignableUsers();
    }
  }, [isAdmin, loadAssignableUsers]);

  const openAuthorizedDrawer = async (record: any) => {
    try {
      const companyId = record?.company?.id;
      const proposalId = record?.id;
      if (!companyId || !proposalId) return;
      setAuthorizedCompanyId(companyId);
      setAuthorizedProposalId(proposalId);
      const resp: any = await ProposalService.get(proposalId);
      const ids: number[] = resp?.data?.authorizedPersonIds || [];
      setSelectedAuthorizedIds(ids);
      setIsAuthorizedDrawerVisible(true);
    } catch (e) {
      console.error('Yetkili kişi verileri alınamadı', e);
      openNotification('error', 'Hata', 'Yetkili kişi bilgileri alınamadı.');
    }
  };

  const handleSaveAuthorizedPersons = async () => {
    if (!authorizedProposalId) return;
    try {
      await ProposalService.updateAuthorizedPersons(authorizedProposalId, { authorizedPersonIds: selectedAuthorizedIds });
      openNotification('success', 'Başarılı', 'Yetkili kişiler kaydedildi.');
      setIsAuthorizedDrawerVisible(false);
      fetchProposals();
    } catch (e) {
      console.error('Yetkili kişiler kaydedilemedi', e);
      openNotification('error', 'Hata', 'Yetkili kişiler kaydedilemedi.');
    }
  };

  const handleCloseAssigneeModal = () => {
    setAssigneeModalVisible(false);
    setAssigneeTargetProposal(null);
    assigneeForm.resetFields();
  };

  const openAssigneeModal = async (proposal: any) => {
    setAssigneeTargetProposal(proposal);
    setAssigneeModalVisible(true);
    setAssigneeModalLoading(true);
    try {
      const [settings, users] = await Promise.all([
        SettingService.listSettings(),
        assignableUsers.length === 0 ? loadAssignableUsers() : Promise.resolve(assignableUsers),
      ]);
      const orderedSettings = reorderAssigneeSettings(settings || []);
      setAssigneeSettings(orderedSettings);
      const initialValues = buildAssigneeInitialValues(proposal, orderedSettings);
      assigneeForm.setFieldsValue(initialValues);
      if (!assignableUsers.length && Array.isArray(users)) {
        setAssignableUsers(users);
      }
    } catch (error) {
      console.error('Sorumlu listesi yüklenirken hata oluştu', error);
      openNotification('error', 'Hata', 'Sorumlu listesi yüklenemedi.');
      handleCloseAssigneeModal();
    } finally {
      setAssigneeModalLoading(false);
    }
  };

  const handleAssigneeUpdate = async () => {
    if (!assigneeTargetProposal) {
      return;
    }
    try {
      const values = await assigneeForm.validateFields();
      const payload = {
        offerAssigneeUserId: values.offerAssigneeUserId ? Number(values.offerAssigneeUserId) : null,
        planAssigneeUserId: values.planAssigneeUserId ? Number(values.planAssigneeUserId) : null,
        legalAssigneeUserId: values.legalAssigneeUserId ? Number(values.legalAssigneeUserId) : null,
        reportAssigneeUserId: values.reportAssigneeUserId ? Number(values.reportAssigneeUserId) : null,
        invoiceAssigneeUserId: values.invoiceAssigneeUserId ? Number(values.invoiceAssigneeUserId) : null,
        financeAssigneeUserId: values.financeAssigneeUserId ? Number(values.financeAssigneeUserId) : null,
      };
      setAssigneeUpdateLoading(true);
      await ProposalService.updateAssignees(assigneeTargetProposal.id, payload);
      openNotification('success', 'Başarılı', 'Sorumlular güncellendi.');
      handleCloseAssigneeModal();
      fetchProposals();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      openNotification('error', 'Hata', extractApiErrorMessage(error, 'Sorumlular güncellenemedi.'));
    } finally {
      setAssigneeUpdateLoading(false);
    }
  };

  const openDueDayModal = (proposal: any) => {
    setDueDayTargetProposal(proposal);
    setIsDueDayModalVisible(true);
    dueDayForm.setFieldsValue({ dueDay: proposal?.dueDay ?? 8 });
  };

  const handleCloseDueDayModal = () => {
    setIsDueDayModalVisible(false);
    setDueDayTargetProposal(null);
    dueDayForm.resetFields();
  };

  const handleDueDayUpdate = async () => {
    if (!dueDayTargetProposal) {
      return;
    }
    try {
      const values = await dueDayForm.validateFields();
      setDueDayUpdateLoading(true);
      await ProposalService.update(dueDayTargetProposal.id, { dueDay: values.dueDay });
      openNotification('success', 'Başarılı', 'Vade günü güncellendi.');
      handleCloseDueDayModal();
      fetchProposals();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      openNotification('error', 'Hata', extractApiErrorMessage(error, 'Vade günü güncellenemedi.'));
    } finally {
      setDueDayUpdateLoading(false);
    }
  };

  const openRenewalSettingsDrawer = (proposal: any) => {
    setRenewalSettingsTargetProposal(proposal);
    setRenewalSettingsOpen(true);
  };

  const closeRenewalSettingsDrawer = () => {
    setRenewalSettingsOpen(false);
    setRenewalSettingsTargetProposal(null);
  };

  const handleRenewalSettingsSubmit = async (values: RenewalSettingsFormValues) => {
    if (!renewalSettingsTargetProposal?.id) {
      openNotification('error', 'Hata', "Teklif ID'si bulunamadı.");
      return;
    }

    try {
      setRenewalSettingsLoading(true);
      await ProposalService.updateRenewalSettings(renewalSettingsTargetProposal.id, {
        renewal: values.renewal ?? false,
        renewalDate: values.renewalDate?.format('YYYY-MM-DD'),
        note: values.note?.trim() || undefined,
      });
      openNotification('success', 'Başarılı', 'Yenileme ayarları güncellendi.');
      closeRenewalSettingsDrawer();
      fetchProposals();
    } catch (error: any) {
      openNotification('error', 'Hata', extractApiErrorMessage(error, 'Yenileme ayarları güncellenemedi.'));
    } finally {
      setRenewalSettingsLoading(false);
    }
  };

  const getManualEmailNotificationType = (mailType: ManualEmailType): NotificationType => {
    if (mailType === 'PLAN') {
      return 'PLAN';
    }
    if (mailType === 'REPORT') {
      return 'REPORT';
    }
    return 'INVOICE';
  };

  const getManualEmailRecipientWarning = (mailType: ManualEmailType | null) => {
    if (mailType === 'PLAN') {
      return 'Teklif adresi ile eşleşen ve PLAN iletişim konusu seçili yetkili kişi bulunmamaktadır.';
    }
    if (mailType === 'REPORT') {
      return 'Teklif adresi ile eşleşen ve RAPOR iletişim konusu seçili yetkili kişi bulunmamaktadır.';
    }
    if (mailType === 'FINANCIAL') {
      return 'Teklif adresi ile eşleşen ve FATURA iletişim konusu seçili yetkili kişi bulunmamaktadır.';
    }
    return 'Uygun e-posta alıcısı bulunmamaktadır.';
  };

  const manualEmailRecipientOptions = useMemo(() => {
    if (!manualEmailTargetProposal || !manualEmailType) {
      return [];
    }
    return getMatchingAuthorizedEmailOptions(
      manualEmailTargetProposal,
      getManualEmailNotificationType(manualEmailType),
    );
  }, [manualEmailTargetProposal, manualEmailType]);

  const getManualEmailModalTitle = (mailType: ManualEmailType | null) => {
    if (mailType === 'PLAN') {
      return 'Planlama Maili Gönder';
    }
    if (mailType === 'REPORT') {
      return 'Kontrol-Rapor Maili Gönder';
    }
    return 'Mali Rapor Gönder';
  };

  const openManualEmailModal = (proposal: any, mailType: ManualEmailType) => {
    const recipientOptions = getMatchingAuthorizedEmailOptions(
      proposal,
      getManualEmailNotificationType(mailType),
    );
    setManualEmailTargetProposal(proposal);
    setManualEmailType(mailType);
    setManualEmailModalVisible(true);
    manualEmailForm.setFieldsValue({ emails: recipientOptions.map((option) => option.value) });
  };

  const closeManualEmailModal = () => {
    setManualEmailModalVisible(false);
    setManualEmailModalLoading(false);
    setManualEmailTargetProposal(null);
    setManualEmailType(null);
    manualEmailForm.resetFields();
  };

  const handleManualEmailSend = async () => {
    if (!manualEmailTargetProposal || !manualEmailType) {
      return;
    }

    try {
      const values = await manualEmailForm.validateFields();
      const emails = Array.from(new Set((values.emails || []).map((email) => email.toLowerCase())));
      setManualEmailModalLoading(true);
      await ProposalService.sendManualEmail(manualEmailTargetProposal.id, {
        emails,
        mailType: manualEmailType,
      });
      const successMessage = manualEmailType === 'REPORT'
        ? `${emails.length} adrese e-posta gönderim kuyruğuna alındı.`
        : `${emails.length} adrese e-posta gönderildi.`;
      openNotification('success', 'Başarılı', successMessage);
      closeManualEmailModal();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      openNotification('error', 'Hata', extractApiErrorMessage(error, 'E-posta gönderilemedi.'));
    } finally {
      setManualEmailModalLoading(false);
    }
  };


  // Tarih aralığı [start, end] (YYYY-MM-DD format)
  const [dateRange, setDateRange] = useState<[string | null, string | null]>([null, null]);
  const [invoiceDateRange, setInvoiceDateRange] = useState<[string | null, string | null]>([null, null]);
  const [paidDateRange, setPaidDateRange] = useState<[string | null, string | null]>([null, null]);
  const [planningPaidDateRange, setPlanningPaidDateRange] = useState<[string | null, string | null]>([null, null]);
  const [approvedPlanningDateRange, setApprovedPlanningDateRange] = useState<[string | null, string | null]>([null, null]);
  const [requestedPlanningDateRange, setRequestedPlanningDateRange] = useState<[string | null, string | null]>([null, null]);
  const [documentUploadedAtRange, setDocumentUploadedAtRange] = useState<[string | null, string | null]>([null, null]);

  const handleDiscountSubmit = async ({
    newTotalPrice,
  }: {
    discountAmount: number;
    newTotalPrice: number;
  }) => {
    if (!selectedProposal || isDiscountSubmitLoading) return;
    setIsDiscountSubmitLoading(true);
    try {
      await ProposalService.updatePrice(selectedProposal.id, {
        newTotalPrice,
      });
      await new Promise((resolve) => setTimeout(resolve, DISCOUNT_PDF_REFRESH_DELAY_MS));
      setDiscountPdfArchiveProposalId(selectedProposal.id);
    } catch (error) {
      setIsDiscountSubmitLoading(false);
      setIsCreate(false);
      console.error('Fiyat güncellenirken hata oluştu:', error);
      openNotification('error', 'Hata', extractApiErrorMessage(error, 'Fiyat güncellenirken hata oluştu.'));
    }
  };

  const handleDiscountPdfArchiveSuccess = () => {
    setDiscountPdfArchiveProposalId(null);
    setIsDiscountSubmitLoading(false);
    setIsDiscountModalVisible(false);
    fetchProposals();
    openNotification('success', 'Başarılı', 'Teklife indirim uygulandı');
  };

  const handleDiscountPdfArchiveError = (error: unknown) => {
    console.error('Teklif PDF’i yeniden oluşturulamadı:', error);
    setDiscountPdfArchiveProposalId(null);
    setIsDiscountSubmitLoading(false);
    setIsDiscountModalVisible(false);
    fetchProposals();
    openNotification('warning', 'Uyarı', PDF_ARCHIVE_REFRESH_WARNING_MESSAGE);
  };

  useEffect(() => {
    // permissions'u localStorage'dan okuyup parse ediyoruz
    const storedPermissions = localStorage.getItem('permissions');
    if (storedPermissions) {
      try {
        const parsedPermissions = JSON.parse(storedPermissions);

        // Eğer JSON içeriği gerçekten bir array ise set et
        if (Array.isArray(parsedPermissions)) {
          setPermissions(parsedPermissions);
        } else {
          console.error('Permissions hatalı formatta:', parsedPermissions);
        }
      } catch (err) {
        console.error('Permissions parse hatası:', err);
      }
    }

    // Statusleri çekelim
    StatusService.getAll()
      .then((response: any) => {
        const filteredStatuses = response.data.filter((status: any) =>
          defaultStatusIds.includes(status.id)
        );
        setStatuses(filteredStatuses);
      })
      .catch((error) => console.error('Statüler yüklenirken hata oluştu:', error));
  }, [defaultStatusIds]);

  useEffect(() => {
    setInputValue(queryParamValue);
    setSearchText(queryParamValue);
    setCurrentPage(1);
    setHasInitializedQueryParam(true);
  }, [queryParamValue]);


  // Arama/filtre değişince sayfayı 1'e sıfırla
  useEffect(() => {
    setCurrentPage(1);
  }, [searchText]);

  const buildRequestParams = useCallback(
    (withPagination: boolean = true) => {
      const [start, end] = dateRange;
      const [invStart, invEnd] = invoiceDateRange;
      const [paidStart, paidEnd] = paidDateRange;
      const [planPaidStart, planPaidEnd] = planningPaidDateRange;
      const [approvedStart, approvedEnd] = approvedPlanningDateRange;
      const [requestedStart, requestedEnd] = requestedPlanningDateRange;
      const [docUploadStart, docUploadEnd] = documentUploadedAtRange;

      let selectedCity: any = "";
      if (selectedCityId) {
        selectedCity = cities.find(city => city.id === selectedCityId);
      }

      let selectedDistrict: any = "";
      if (selectedDistrictId) {
        selectedDistrict = districts.find(district => district.id === selectedDistrictId);
      }

      let selectedCityName = selectedDistrictId === null ? (selectedCity ? normalizeTurkish(selectedCity?.name) : "") : "";

      let accountId = "";
      if (onlyMyCompanies && userId) {
        accountId = userId;
      }

      const params: any = {
        accountId: onlyMyCompanies ? accountId : selectedCustomerId || "",
        companyId: selectedCompanyId || "",
        company: normalizeTurkish(searchText),
        city: selectedCityName,
        district: selectedDistrict ? normalizeTurkish(selectedDistrict?.name) : "",
        statuIds: defaultStatusIds.join(','),
        sort: sortField,
        onlyPartner: (paymentType == 1 || paymentType == 2) ? true : false,
        direction: sortDirection,
        ...(type === 5 && { isPaid: paymentType === 2 }),
        ...(start && end && { startDate: start, endDate: end }),
        ...(invStart && invEnd && { startInvoiceDate: invStart, endInvoiceDate: invEnd }),
        ...(paidStart && paidEnd && { startPaidDate: paidStart, endPaidDate: paidEnd }),
        ...(planPaidStart && planPaidEnd && { startPlanningPaidDate: planPaidStart, endPlanningPaidDate: planPaidEnd }),
        ...(approvedStart && approvedEnd && { startApprovedPlanningDate: approvedStart, endApprovedPlanningDate: approvedEnd }),
        ...(requestedStart && requestedEnd && { startRequestedPlanningDate: requestedStart, endRequestedPlanningDate: requestedEnd }),
        ...(docUploadStart && docUploadEnd && { startDocumentUploadedAt: docUploadStart, endDocumentUploadedAt: docUploadEnd }),
      };

      if (withPagination) {
        params.page = currentPage - 1;
        params.size = pageSize;
      }

      return params;
    },
    [
      approvedPlanningDateRange,
      cities,
      currentPage,
      dateRange,
      defaultStatusIds,
      documentUploadedAtRange,
      districts,
      invoiceDateRange,
      onlyMyCompanies,
      pageSize,
      paidDateRange,
      paymentType,
      planningPaidDateRange,
      requestedPlanningDateRange,
      searchText,
      selectedCityId,
      selectedCompanyId,
      selectedCustomerId,
      selectedDistrictId,
      sortDirection,
      sortField,
      type,
      userId
    ]
  );

  const calculateOfferingMinutes = useCallback((record: any) => {
    const totals = {
      machineMinutes: 0,
      electricMinutes: 0,
      hygieneMinutes: 0,
    };
    const proposalAttributes = record?.attributes || [];

    proposalAttributes.forEach((proposalAttribute: any) => {
      const matched = attributes?.find((attribute: any) => attribute?.id === proposalAttribute?.attributeId);
      if (!matched) return;

      const fieldTime = Number(matched.fieldControlTime) || 0;
      const quantity = Number(proposalAttribute.quantity) || 0;
      const period = Number(proposalAttribute.period) || 0;
      const totalMinutes = fieldTime * period * quantity;

      if (matched.offering?.name === 'Makine') {
        totals.machineMinutes += totalMinutes;
      } else if (matched.offering?.name === 'Elektrik') {
        totals.electricMinutes += totalMinutes;
      } else if (matched.offering?.name === 'İş Hijyeni') {
        totals.hygieneMinutes += totalMinutes;
      }
    });

    return totals;
  }, [attributes]);

  const fetchSummaryTotals = useCallback(async () => {
    if (!shouldShowSummaryTotals) {
      setSummaryTotals(EMPTY_SUMMARY_TOTALS);
      setSummaryLoading(false);
      return;
    }

    const requestId = summaryRequestId.current + 1;
    summaryRequestId.current = requestId;
    setSummaryLoading(true);

    try {
      const response: any = await ProposalService.getAllWithoutPagination(buildRequestParams(false));
      if (summaryRequestId.current !== requestId) {
        return;
      }

      const proposals: any[] = Array.isArray(response?.data) ? response.data : [];
      const nextTotals = proposals.reduce((acc, proposal) => {
        const totalPrice = Number(proposal?.totalPrice);
        if (!Number.isNaN(totalPrice)) {
          acc.totalPrice += totalPrice;
        }

        const offeringMinutes = calculateOfferingMinutes(proposal);
        acc.machineMinutes += offeringMinutes.machineMinutes;
        acc.electricMinutes += offeringMinutes.electricMinutes;
        acc.hygieneMinutes += offeringMinutes.hygieneMinutes;

        return acc;
      }, { ...EMPTY_SUMMARY_TOTALS });

      setSummaryTotals(nextTotals);
    } catch (error) {
      if (summaryRequestId.current === requestId) {
        setSummaryTotals(EMPTY_SUMMARY_TOTALS);
      }
      console.error('Proje takip özet toplamları yüklenirken hata oluştu:', error);
    } finally {
      if (summaryRequestId.current === requestId) {
        setSummaryLoading(false);
      }
    }
  }, [buildRequestParams, calculateOfferingMinutes, shouldShowSummaryTotals]);

  const fetchProposals = async () => {
    setLoading(true);
    setFilteredProposals([]);
    setTotalItems(0);
    logoCache.current.clear();
    pendingRequests.current.clear();

    const params = buildRequestParams(true);

    try {
      const response: any = await ProposalService.getAll(params);
      setFilteredProposals(response?.data.content);
      setTotalItems(response.data.totalElements);
      setIsLoaded(true);
    } catch (error) {
      console.error('Teklif verileri alınırken hata oluştu:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (
      !isPlanningModalVisible &&
      !isUploadModalVisible &&
      !isDocumentModalVisible &&
      !isPaymentModalVisible
    )
      if (isLoaded) {
        fetchProposals();
        fetchSummaryTotals();
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [

    isPlanningModalVisible,
    isUploadModalVisible,
    isDocumentModalVisible,
    isPaymentModalVisible
    // permissions değişince tekrar çek
  ]);

  useEffect(() => {
    if (queryParamValue && !hasInitializedQueryParam) {
      return;
    }
    fetchProposals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    pageSize,
    sortField,
    sortDirection,
    selectedPaidStatus,
    userId,
    filter,
    searchText,
    queryParamValue,
    hasInitializedQueryParam,
    // permissions, // permissions değişince tekrar çek
  ]);

  useEffect(() => {
    if (queryParamValue && !hasInitializedQueryParam) {
      return;
    }
    fetchSummaryTotals();
  }, [
    fetchSummaryTotals,
    hasInitializedQueryParam,
    queryParamValue,
  ]);

  const handleTableChange = (pagination: any, _filters: any, sorter: any) => {
    setSortField(sorter.field || sortFieldProp);

    if (sorter.order) {
      setSortDirection(sorter.order === 'descend' ? 'DESC' : 'ASC');
    }

    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize || initialPageSize);
  };

  const handleOpenInvioceModal = () => {
    setIsInvoiceModalVisible(true);
  };

  const handleCloseInvoiceModal = () => {
    setIsInvoiceModalVisible(false);
  };

  const minutesToDaysCeil = (minutes: number) => {
    if (minutes <= 0) return '-';
    return `${Math.ceil(minutes / 480)}`;
  };

  const computeOfferingsForExport = (record: any) => {
    const proposalAttributes = record?.attributes || [];
    let totalMakineMin = 0;
    let totalIsHijyeniMin = 0;
    let totalElektrikMin = 0;

    proposalAttributes.forEach((pa: any) => {
      const matched = attributes?.find((a: any) => a?.id === pa?.attributeId);
      if (!matched) return;
      const fieldTime = matched.fieldControlTime || 0;
      const qty = pa.quantity || 0;
      const period = pa.period || 0;
      const partial = fieldTime * (period * qty);
      if (matched.offering?.name === 'Makine') {
        totalMakineMin += partial;
      } else if (matched.offering?.name === 'İş Hijyeni') {
        totalIsHijyeniMin += partial;
      } else if (matched.offering?.name === 'Elektrik') {
        totalElektrikMin += partial;
      }
    });

    let totalMakinePrice = 0;
    let totalIsHijyeniPrice = 0;
    let totalElektrikPrice = 0;
    const logs = record?.priceCalculationResponse?.offeringLogs || [];
    logs.forEach((log: any) => {
      if (log.offeringName === 'Makine') {
        totalMakinePrice += log.finalPrice || 0;
      } else if (log.offeringName === 'İş Hijyeni') {
        totalIsHijyeniPrice += log.finalPrice || 0;
      } else if (log.offeringName === 'Elektrik') {
        totalElektrikPrice += log.finalPrice || 0;
      }
    });

    const totalPriceSum = totalMakinePrice + totalElektrikPrice + totalIsHijyeniPrice;
    if (totalPriceSum > 0) {
      const discountedPrice = record?.discountedPrice || 0;
      const oranMakine = totalMakinePrice / totalPriceSum;
      const oranElektrik = totalElektrikPrice / totalPriceSum;
      const oranHijyen = totalIsHijyeniPrice / totalPriceSum;
      const indirimMakine = Math.round(discountedPrice * oranMakine);
      const indirimElektrik = Math.round(discountedPrice * oranElektrik);
      const indirimHijyen = discountedPrice - indirimMakine - indirimElektrik;
      totalMakinePrice -= indirimMakine;
      totalElektrikPrice -= indirimElektrik;
      totalIsHijyeniPrice -= indirimHijyen;
    }

    return {
      machinePrice: totalMakinePrice,
      hygienePrice: totalIsHijyeniPrice,
      electricPrice: totalElektrikPrice,
      machineDays: minutesToDaysCeil(totalMakineMin),
      hygieneDays: minutesToDaysCeil(totalIsHijyeniMin),
      electricDays: minutesToDaysCeil(totalElektrikMin),
    };
  };

  const getExcelFileName = useCallback(() => {
    if (type === 5) {
      if (paymentType === 1) {
        return 'Ödemesi Planlanmış Projeler';
      }
      if (paymentType === 2) {
        return 'Ödemesi Tamamlanan Projeler';
      }
      return 'Ödeme Listesi';
    }

    switch (type) {
      case 1:
        return 'Sözleşme Listesi';
      case 2:
        return 'Planlama Listesi';
      case 7:
        return 'Yasal Bildirim (İSG-Katip) Listesi';
      case 3:
        return 'Kontrol ve Raporlama Listesi';
      case 4:
        return 'Tamamlanan Projeler';
      default:
        return 'ProjectFollowUp';
    }
  }, [paymentType, type]);

  const handleExportExcel = useCallback(async () => {
    try {
      setExporting(true);
      const params = buildRequestParams(false);
      const response: any = await ProposalService.getAllWithoutPagination(params);
      const data: any[] = Array.isArray(response?.data) ? response.data : [];
      const exportData = data.map((proposal: any) => {
        const addressTitle = proposal?.address?.title ?? '';
        const stateName = proposal?.address?.state || '';
        const cityName = proposal?.address?.city || '';
        const location = [cityName, stateName].filter((s: string) => !!s).join(' / ');
        const accountName = proposal?.account
          ? `${proposal.account.firstName || ''} ${proposal.account.lastName || ''}`.trim()
          : '';

        if (type === 5 && paymentType === 1) {
          const paymentPlanDate = proposal?.invoiceDate
            ? dayjs(proposal.invoiceDate).add(31, 'days').format('DD/MM/YYYY')
            : '';

          return {
            'Teklif Numarası': proposal?.proposalNumber ?? '',
            'Teklif Tarihi': proposal?.createdAt ? formatDate(proposal.createdAt) : '',
            'Firma Adı': proposal?.company?.name ?? '',
            'Adres Başlığı': addressTitle,
            'İl / İlçe': location,
            'Teklif Tutarı': typeof proposal?.totalPrice === 'number' ? formatToTL(proposal.totalPrice) : '-',
            'Fatura Tutarı': typeof proposal?.invoiceAmount === 'number' ? formatToTL(proposal.invoiceAmount) : '-',
            'Fatura Tarihi': proposal?.invoiceDate ? formatDateString(proposal.invoiceDate) : '',
            'Ödenecek Tutar': typeof proposal?.incomeSharePrice === 'number' ? formatToTL(proposal.incomeSharePrice) : '-',
            'Ödeme Planı': paymentPlanDate,
            'Kullanıcı Adı Soyadı': accountName,
          };
        }

        if (type === 5 && paymentType === 2) {
          const paymentPlanDate = proposal?.invoiceDate
            ? dayjs(proposal.invoiceDate).add(31, 'days').format('DD/MM/YYYY')
            : '';

          return {
            'Teklif Numarası': proposal?.proposalNumber ?? '',
            'Teklif Tarihi': proposal?.createdAt ? formatDate(proposal.createdAt) : '',
            'Firma Adı': proposal?.company?.name ?? '',
            'Adres Başlığı': addressTitle,
            'İl / İlçe': location,
            'Teklif Tutarı': typeof proposal?.totalPrice === 'number' ? formatToTL(proposal.totalPrice) : '-',
            'Fatura Tutarı': typeof proposal?.invoiceAmount === 'number' ? formatToTL(proposal.invoiceAmount) : '-',
            'Fatura Tarihi': proposal?.invoiceDate ? formatDateString(proposal.invoiceDate) : '',
            'Ödenen Tutar': typeof proposal?.incomeSharePrice === 'number' ? formatToTL(proposal.incomeSharePrice) : '-',
            'Ödeme Planı': paymentPlanDate,
            'Ödeme Tarihi': proposal?.paidDate ? formatDate(proposal.paidDate) : '',
            'Kullanıcı Adı Soyadı': accountName,
          };
        }

        const offering = computeOfferingsForExport(proposal);
        const controlDates = proposal?.approvedPlanningStartDate
          ? `${formatDate(proposal.approvedPlanningStartDate)} - ${formatDate(proposal.approvedPlanningEndDate)}`
          : '';

        const row: any = {
          'Teklif Numarası': proposal?.proposalNumber ?? '',
          'Teklif Tarihi': proposal?.createdAt ? formatDate(proposal.createdAt) : '',
          'Firma Adı': proposal?.company?.name ?? '',
          'Adres Başlığı': addressTitle,
          'İl / İlçe': location,
          'Teklif Tutarı': typeof proposal?.totalPrice === 'number' ? formatToTL(proposal.totalPrice) : '-',
          'Makine Tutarı': offering.machinePrice ? formatToTL(offering.machinePrice) : '-',
          'Makine Uzman / Gün': offering.machineDays,
          'Elektrik Tutarı': offering.electricPrice ? formatToTL(offering.electricPrice) : '-',
          'Elektrik Uzman / Gün': offering.electricDays,
          'İş Hijyeni Tutarı': offering.hygienePrice ? formatToTL(offering.hygienePrice) : '-',
          'İş Hijyeni Uzman / Gün': offering.hygieneDays,
          'Kullanıcı Adı Soyadı': accountName,
        };

        if (type === 4) {
          const invoiceAmount = proposal?.invoiceAmount ?? proposal?.totalPrice;
          row['Sözleşme Yükleme Tarihi'] = proposal?.documentUploadedAt ? formatDate(proposal.documentUploadedAt) : '';
          row['Kontrol Tarihleri'] = controlDates;
          row['Fatura Tarihi'] = proposal?.invoiceDate ? formatDateString(proposal.invoiceDate) : '';
          row['Fatura Tutarı'] = typeof invoiceAmount === 'number' ? formatToTL(invoiceAmount) : '-';
        } else {
          row['Kontrol Tarihleri'] = controlDates;
        }

        return row;
      });
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'ProjectFollowUp');
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
      saveAs(blob, `${getExcelFileName()}.xlsx`);
    } catch (error) {
      console.error('Teklifler dışa aktarılırken hata oluştu:', error);
      openNotification('error', 'Hata', 'Teklifler dışa aktarılırken bir hata oluştu.');
    } finally {
      setExporting(false);
    }
  }, [buildRequestParams, attributes, getExcelFileName, paymentType, type]);

  const enrichProposalsWithLogos = async (proposals: any[]) => {
    return Promise.all(
      proposals.map(async (proposal) => {
        const { company } = proposal;
        if (company.imageId) {
          if (logoCache.current.has(company.imageId)) {
            return {
              ...proposal,
              company: {
                ...company,
                logoUrl: logoCache.current.get(company.imageId),
              },
            };
          } else if (pendingRequests.current.has(company.imageId)) {
            await pendingRequests.current.get(company.imageId);
            return {
              ...proposal,
              company: {
                ...company,
                logoUrl: logoCache.current.get(company.imageId),
              },
            };
          } else {
            const logoPromise = CompanyService.getImage(company.imageId)
              .then((logoResponse) => {
                const logoUrl = logoResponse.data.preSignUrl;
                logoCache.current.set(company.imageId, logoUrl);
                pendingRequests.current.delete(company.imageId);
                return logoUrl;
              })
              .catch((error) => {
                console.error('Logo yüklenirken hata oluştu:', error);
                pendingRequests.current.delete(company.imageId);
                return null;
              });

            pendingRequests.current.set(company.imageId, logoPromise);

            const logoUrl = await logoPromise;
            return { ...proposal, company: { ...company, logoUrl } };
          }
        }
        return proposal;
      })
    );
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target?.value ?? '';
    setInputValue(searchValue);
    handleSearch(searchValue);
  };

  const handleSearch = useCallback(
    debounce((value: string) => {
      setSearchText(value);
      setCurrentPage(1);
    }, 500),
    []
  );

  const handleApprove = async (
    proposalId: number | null,
    statusId: number,
    successMessage?: string,
    payload?: any
  ) => {
    if (proposalId === null) return;
    try {
      await ProposalService.updateStatus(proposalId, statusId, payload);
      if (successMessage) {
        openNotification('success', 'Başarılı', successMessage);
      } else if (statusId === 6) {
        openNotification('success', 'Başarılı', 'Planlama iptal edildi.');
      } else if (statusId === 7) {
        openNotification('success', 'Başarılı', 'Yasal bildirim iptal edildi.');
      }
      if (statusId === 2) {
        openNotification('success', 'Başarılı', 'Teklif olumsuz sonuçlandırıldı.');
      }
      if (statusId === 10) {
        openNotification('success', 'Başarılı', 'Sözleşme onayı iptal edildi.');
      }
      if (!successMessage && statusId === 5) {
        openNotification('success', 'Başarılı', 'Rapor ve fatura silindi.');
      }
      fetchProposals();
      console.log(`Teklif ${proposalId} başarıyla güncellendi.`);
    } catch (error) {
      const message = extractApiErrorMessage(error, 'Teklif güncellenirken hata oluştu.');
      const isCompanyWarning = message === COMPANY_INFO_WARNING_MESSAGE;
      openNotification(isCompanyWarning ? 'warning' : 'error', isCompanyWarning ? 'Uyarı' : 'Hata', message);
      console.error(`Teklif güncellenirken hata oluştu:`, error);
    }
  };

  const handleCancelContract = async (proposalId: number | null) => {
    if (proposalId === null) return;
    try {
      await ProposalService.cancelContract(proposalId);
      openNotification('success', 'Başarılı', 'Sözleşme iptal edildi.');
      fetchProposals();
    } catch (error) {
      const message = extractApiErrorMessage(error, 'Sözleşme iptal edilirken hata oluştu.');
      openNotification('error', 'Hata', message);
    }
  };

  const openNegativeProposalReasonModal = (proposal: any) => {
    setNegativeModalProposal(proposal);
    setNegativeModalVisible(true);
    setNegativeModalLoading(false);
  };

  const closeNegativeProposalReasonModal = () => {
    setNegativeModalVisible(false);
    setNegativeModalProposal(null);
    setNegativeModalLoading(false);
  };

  const handleNegativeProposalSubmit = async (values: NegativeReasonFormValues) => {
    if (!negativeModalProposal) {
      return;
    }
    setNegativeModalLoading(true);
    try {
      await handleApprove(negativeModalProposal.id, 2, undefined, {
        negativeReasonId: values.negativeReasonId,
        negativeReasonText: values.negativeReasonText?.trim(),
      });
      closeNegativeProposalReasonModal();
    } finally {
      setNegativeModalLoading(false);
    }
  };
  const openPaymentModal = (proposalId: number, type: any) => {
    setSelectedProposalId(proposalId);
    setIsPaymentModalVisible(true);
    setPaymentModalType(type);
  };

  const openCategoryDrawer = async (record: any) => {
    if (!record?.id) {
      return;
    }
    const ok = await canCompleteLegalNotification(record);
    if (!ok) {
      return;
    }
    setCategoryDrawerProposalId(record.id);
    setIsCategoryDrawerVisible(true);
  };

  const closeCategoryDrawer = () => {
    setIsCategoryDrawerVisible(false);
    setCategoryDrawerProposalId(null);
    fetchProposals();
  };

  const openReportApprovalDrawer = (record: any) => {
    setReportApprovalProposalId(record?.id ?? null);
    setReportApprovalProposalNumber(record?.proposalNumber ?? null);
    setIsReportApprovalDrawerVisible(true);
  };

  const closeReportApprovalDrawer = () => {
    setIsReportApprovalDrawerVisible(false);
    setReportApprovalProposalId(null);
    setReportApprovalProposalNumber(null);
  };

  const handleReportApprovalSummaryChange = useCallback((proposalId: number, summary: {
    completedInspectionTaskCount: number;
    approvedInspectionTaskCount: number;
    inspectionTaskCount: number;
  }) => {
    setFilteredProposals((prev) => prev.map((proposal: any) => (
      proposal?.id === proposalId
        ? (() => {
          const currentInspectionTaskCount = Number(proposal?.inspectionTaskCount ?? 0);
          const nextInspectionTaskCount = Number(summary.inspectionTaskCount ?? 0);

          return {
            ...proposal,
            completedInspectionTaskCount: summary.completedInspectionTaskCount,
            approvedInspectionTaskCount: summary.approvedInspectionTaskCount,
            inspectionTaskCount: Math.max(currentInspectionTaskCount, nextInspectionTaskCount),
          };
        })()
        : proposal
    )));
  }, []);

  let columns: any[] = [
    {
      title: 'Firma Adı',
      dataIndex: ['company', 'name'],
      key: 'companyName',
      render: (text: any, record: any) => {
        const combinedText = `${record?.address?.state || ''}${record?.address?.state && record?.address?.city ? '/' : ''}${record?.address?.city || ''}`;
        const controlTitle = record?.address?.title;
        return (
          <div className="d-flex align-items-start">
            <div className="avatar-image-logo" style={{ marginRight: 10, marginTop: 4 }}>
              <Avatar
                className="avatar-image-logo"
                style={{ backgroundColor: '#f0f0f0', color: '#000', cursor: 'pointer' }}
                onClick={() => record?.company?.id && navigate(`/companies/${record.company.id}`)}
              >
                {record?.company?.name.charAt(0).toUpperCase()}
              </Avatar>
            </div>
            <div>
              <Tooltip
                placement="topLeft"
                title={
                  <>
                    {text.toUpperCase()} <br />
                    {controlTitle && <>{controlTitle}<br /></>}
                    {combinedText}
                  </>
                }
              >
                <Text
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: 'inline-block',
                    width: '220px',
                    cursor: 'pointer',
                    fontWeight: 600,
                  }}
                  onClick={() => record?.company?.id && navigate(`/companies/${record.company.id}`)}
                >
                  {text.toUpperCase()}
                </Text>
              </Tooltip>
              {controlTitle && (
                <div style={{ color: '#666', fontSize: 12, marginTop: 4 }}>
                  {controlTitle.length > 60 ? (
                    <Tooltip title={controlTitle}>
                      <span>{controlTitle.slice(0, 60)}...</span>
                    </Tooltip>
                  ) : (
                    <span>{controlTitle}</span>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      },
    },

    ...(permissions.includes('proposal.show')
      ? [
        {
          title: 'Kullanıcı Ad Soyad',
          key: 'accountOwner',
          render: (_: any, record: any) =>
            record.account ? (
              <Link to={`/users/${record.account.id}`}>
                {record.account.firstName} {record.account.lastName}
              </Link>
            ) : (
              'Bilgi Yok'
            ),
        },
      ]
      : []),
    {
      title: 'Teklif No',
      dataIndex: 'proposalNumber',
      key: 'proposalNumber',
      sorter: true,
      render: (proposalNumber: string) => {
        const proposalNumberText = proposalNumber?.trim();
        if (!proposalNumberText) return <span>-</span>;

        return (
          <Link
            to={`/proposals?search=${encodeURIComponent(proposalNumberText)}`}
            onClick={(event) => event.stopPropagation()}
            style={{ color: 'blue', fontWeight: 500 }}
          >
            {proposalNumberText}
          </Link>
        );
      },
    },
    ...(!isCustomer
      ? [
        {
          title: 'Sorumlu',
          key: 'assignees',
          width: 220,
          render: (_: any, record: any) => <AssigneeSummary record={record} statusId={record.statusId} />,
        },
      ]
      : []),

    {
      title: 'Teklif Tarihi',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      render: (date: string) => <Text>{formatDate(date)}</Text>,
    },



    ...((type === 1 || type === 2 || type === 4 || type === 5 || type === 7)
      ? [
        {
          title: 'Teklif Tutarı',
          dataIndex: 'totalPrice',
          key: 'totalPrice',
          sorter: true,
          width: 130,
          render: (price: any, record: any) => (
            <Text>{formatToTL(record.totalPrice)}</Text>
          ),
        },
      ]
      : []),
    ...((type === 3 || type === 5)
      ? [
        {
          title: type === 3 ? 'Teklif Tutarı' : 'Fatura Tutarı',
          dataIndex: 'totalPrice',
          key: 'totalPrice',
          sorter: true,
          width: 130,
          render: (price: any, record: any) => (
            <Text>{(type === 5) && formatToTL(record.invoiceAmount)}
              {(type === 3) && formatToTL(price)}</Text>
          ),
        },
      ]
      : []),
    ...(type === 2
      ? [
        {
          title: 'Planlama Talep Tarihleri',
          dataIndex: 'requestedPlanningStartDate',
          key: 'requestedPlanningStartDate',
          sorter: true,
          render: (_: any, record: any) => {
            return record.requestedPlanningStartDate ? (
              <Text>
                {formatDate(record.requestedPlanningStartDate)}<br />
                {formatDate(record.requestedPlanningEndDate!)}
              </Text>
            ) : (
              <Text>Talep Edilmemiş</Text>
            );
          },
        },
      ]
      : []),
    ...(type === 5
      ? [
        {
          title: 'Fatura Tarihi',
          dataIndex: 'invoiceDate',
          key: 'invoiceDate',
          sorter: true,
          render: (date: string) => <Text>{formatDateString(date)}</Text>,
        },
      ]
      : []),
    ...(type === 1 || type === 4
      ? [
        {
          title: 'Sözleşme Yükleme Tarihi',
          dataIndex: 'documentUploadedAt',
          key: 'documentUploadedAt',
          sorter: true,
          render: (date: string) => <Text>{formatDate(date)}</Text>,
        },
      ]
      : []),
    ...(type === 5
      ? [
        {
          title: paymentType === 1 ? 'Ödenecek Tutar' : 'Ödenen Tutar',
          dataIndex: 'incomeSharePrice',
          key: 'incomeSharePrice',
          sorter: true,
          render: (text: any) => <Text>{formatToTL(text)}</Text>,
        },
      ]
      : []),

    ...(type === 3 || type === 4 || type === 7
      ? [
        {
          title: 'Kontrol Tarihleri',
          dataIndex: 'approvedPlanningEndDate',
          key: 'approvedPlanningEndDate',
          render: (_: any, record: any) => {
            return record.approvedPlanningStartDate ? (
              <Text>
                {formatDate(record.approvedPlanningStartDate)}<br />
                {formatDate(record.approvedPlanningEndDate!)}
              </Text>
            ) : (
              <Text>Planlanmamış</Text>
            );
          },
        },
      ]
      : []),
    ...(type === 3 && defaultStatusIds.includes(5)
      ? [
        {
          title: 'Kontrol/Onaylı/Toplam',
          key: 'inspectionTaskCompletion',
          width: 170,
          render: (_: any, record: any) => {
            const totalCount = Number(record?.inspectionTaskCount ?? 0);
            const completedCount = Number(record?.completedInspectionTaskCount ?? 0);
            const approvedCount = Number(record?.approvedInspectionTaskCount ?? 0);
            return (
              <Button
                type="link"
                size="small"
                style={{ padding: 0, height: 'auto' }}
                onClick={() => openReportApprovalDrawer(record)}
              >
                {`${completedCount}/${approvedCount}/${totalCount}`}
              </Button>
            );
          },
        },
      ]
      : []),
    ...(type === 4
      ? [
        {
          title: 'Fatura Tarihi',
          dataIndex: 'invoiceDate',
          key: 'invoiceDate',
          sorter: true,
          render: (date: string) => <Text>{formatDateString(date)}</Text>,
        },
        {
          title: 'Fatura Tutarı',
          dataIndex: 'totalPrice',
          key: 'totalPrice',
          sorter: true,
          width: 130,
          render: (price: any, record: any) => (
            <Text>{formatToTL(record.invoiceAmount ?? price)}</Text>
          ),
        },
      ]
      : []),
    // Planlama Tarihi + "Saha Kontrol" (her satır için quantity * fieldControlTime toplamı)
    ...(type === 1 || type === 2
      ? [
        {
          title: 'Makine - Elektrik - İş Hijyeni',
          key: 'sahaKontrol',
          render: (_: any, record: any) => {
            const proposalAttributes = record.attributes || [];

            // 3 offering’e ait “toplam dakika” tutalım
            let totalMakine = 0;
            let totalIsHijyeni = 0;
            let totalElektrik = 0;

            // Teklif attrib’lerinden Makine / İş Hijyeni / Elektrik sahasını topla
            proposalAttributes.forEach((pa: any) => {
              const matched = attributes?.find((a: any) => a?.id === pa?.attributeId);
              if (matched) {
                const fieldTime = matched.fieldControlTime || 0;
                const qty = pa.quantity || 0;
                const period = pa.period || 0;
                const partial = fieldTime * (period * qty);

                if (matched.offering?.name === 'Makine') {
                  totalMakine += partial;
                } else if (matched.offering?.name === 'İş Hijyeni') {
                  totalIsHijyeni += partial;
                } else if (matched.offering?.name === 'Elektrik') {
                  totalElektrik += partial;
                }
              }
            });

            // Yukarı yuvarlama fonksiyonu
            const asDaysCeil = (minutes: number) => {
              if (minutes <= 0) return 0;
              const days = Math.ceil(minutes / 480);
              return `${days}`;
            };

            const asDaysCalc = (minutes: number) => {
              if (minutes <= 0) return 0;
              const days = minutes / 480;
              return days.toFixed(2);
            };

            // Metinler
            const textMakine = totalMakine > 0 ? `${asDaysCeil(totalMakine)}` : '-';
            const textHijyen = totalIsHijyeni > 0 ? `${asDaysCeil(totalIsHijyeni)}` : '-';
            const textElektrik = totalElektrik > 0 ? `${asDaysCeil(totalElektrik)}` : '-';

            // Eğer tümü 0'sa tek “-”
            if (
              textMakine === '-' &&
              textHijyen === '-' &&
              textElektrik === '-'
            ) {
              return '-';
            }
            return (<span>{textMakine} ({asDaysCalc(totalMakine)}) / {textElektrik} ({asDaysCalc(totalElektrik)}) / {textHijyen} ({asDaysCalc(totalIsHijyeni)})</span>);
          },
        },
      ]
      : []),
    ...(type === 5
      ? [
        {
          title: 'Ödeme Planı',
          dataIndex: 'invoiceDate',
          key: 'invoiceDateStatus',
          sorter: true,
          render: (invoiceDate: string) => {
            if (!invoiceDate) {
              return <Text>-</Text>;
            }
            const today = dayjs();
            const adjustedDueDate = dayjs(invoiceDate).add(31, 'days');
            let diff = adjustedDueDate.diff(today, 'days');

            let colorHex = '#52c41a'; // Yeşil varsayılan
            let tooltipText: string | null = null;

            if (diff <= 0) {
              colorHex = '#ff4d4f'; // Kırmızı
              diff = 0;
              tooltipText = 'Süresi Geçmiş';
            } else if (diff <= 10) {
              colorHex = '#fa8c16'; // Turuncu
            }

            const color = tinycolor(colorHex);
            const background = color.setAlpha(0.1).toRgbString();
            const borderColor = color.setAlpha(0.5).toRgbString();

            const tagEl = (
              <Tag
                style={{
                  color: colorHex,
                  backgroundColor: background,
                  borderColor: borderColor,
                  borderRadius: '12px',
                  padding: '5px 10px',
                  fontSize: '14px',
                  display: 'inline-block',
                  minWidth: '50px',
                  textAlign: 'center',
                  fontWeight: 500,
                }}
              >
                {adjustedDueDate.format('DD/MM/YYYY')}
              </Tag>
            );

            if (tooltipText) {
              return <Tooltip title={tooltipText}>{tagEl}</Tooltip>;
            }
            return tagEl;
          },
        }
      ]
      : []),
    ...(type === 5 && paymentType === 2
      ? [
        {
          title: 'Ödeme Tarihi',
          dataIndex: 'paidDate',
          key: 'paidDate',
          sorter: true,
          render: (date: string) => <Text>{formatDate(date)}</Text>,
        },
      ]
      : []),
    // İşlemler
    {
      title: '',
      key: 'action',
      render: (_: any, record: any) =>
        <>
          {type !== 5 && (
            <Row justify="center" align="middle">
              <Tooltip title="Yetkili Kişiler">
                <Button
                  type="default"
                  className="action-button icon-action-button"
                  style={{ marginRight: 8 }}
                  onClick={() => openAuthorizedDrawer(record)}
                >
                  <UserListIcon style={{ fontSize: 20 }} />
                </Button>
              </Tooltip>
              <Dropdown
                overlay={
                  <Menu>
                    {isAdmin && (
                      <Menu.Item key="updateAssignees" onClick={() => openAssigneeModal(record)}>
                        Sorumlu Güncelle
                      </Menu.Item>
                    )}
                    <Menu.Item key="editDueDay" onClick={() => openDueDayModal(record)}>
                      Vade Günü Düzenle
                    </Menu.Item>
                    <Menu.Item key="renewalSettings" onClick={() => openRenewalSettingsDrawer(record)}>
                      Yenileme Ayarları
                    </Menu.Item>
                    <Menu.Item key="notesAndTasks" onClick={() => handleOpenNotesDrawer(record)}>
                      İşlem Geçmişi
                    </Menu.Item>
                    {type === 1 && (
                      <>
                        <Menu.Item>
                          <ProposalView proposalId={record.id} proposalUrl={record.proposalUrl} />
                        </Menu.Item>
                        {isAdmin && (<><Menu.Item key="revise" onClick={() => navigate(`/proposals/create?proposalId=${record.id}`)}>Teklifi Düzenle</Menu.Item>
                          <Menu.Item onClick={() => applyDiscount(record)}>İndirim Uygula</Menu.Item>
                          <Menu.Item key="rejectProposal" onClick={() => openNegativeProposalReasonModal(record)}>
                            Olumsuz Sonlandır
                          </Menu.Item>
                        </>)}

                        <Menu.Item
                          key="uploadModal"
                          onClick={() => openUploadModal(record.id, 2)}
                          disabled={!record.documentId}
                        >
                          Sözleşmeyi Görüntüle
                        </Menu.Item>
                        <Menu.Item
                          key="editUploadModal"
                          onClick={() => openUploadModal(record.id, 1)}
                          disabled={!record.documentId}
                        >
                          Sözleşmeyi Güncelle
                        </Menu.Item>
                        {record.statusId === 6 ? (
                          <Popconfirm placement="left"
                            title="Onayı iptal etmek istediğinize emin misiniz?"
                            onConfirm={() => handleApprove(record.id, 10)}
                            okText="Evet"
                            cancelText="Hayır"
                          >
                            <Menu.Item key="revertApprove">
                              Sözleşme Onayını İptal Et
                            </Menu.Item>
                          </Popconfirm>
                        ) : (
                          <>
                            <Menu.Item
                              key="approve"
                              onClick={() => openDocumentModal(record.id)}
                              disabled={!record.documentId}
                            >
                              Sözleşmeyi Kontrol Et
                            </Menu.Item>
                            <Popconfirm
                              placement="left"
                              title="Sözleşmeyi iptal etmek istediğinize emin misiniz?"
                              onConfirm={() => handleCancelContract(record.id)}
                              okText="Evet"
                              cancelText="Hayır"
                            >
                              <Menu.Item key="cancelContract" disabled={!record.documentId}>
                                Sözleşmeyi İptal Et
                              </Menu.Item>
                            </Popconfirm>
                          </>
                        )}

                      </>
                    )}
                    {type === 4 && (
                      <>
                        <Menu.Item>
                          <ProposalView proposalId={record.id} proposalUrl={record.proposalUrl} />
                        </Menu.Item>
                        <Menu.Item
                          key="uploadModal"
                          onClick={() => openUploadModal(record.id, 2)}
                          disabled={!record.documentId}
                        >
                          Sözleşmeyi Görüntüle
                        </Menu.Item>
                        <Menu.Item onClick={() => navigateDetailPlan(record)}>
                          Planı Görüntüle
                        </Menu.Item>
                        <Menu.Item
                          key="reportDetails"
                          onClick={() => navigate(`/proposals/report?proposalId=${record.id}`)}
                        >
                          Rapor Detayları
                        </Menu.Item>
                        <Menu.Item
                          key="categoryList"
                          onClick={() => openCategoryDrawer(record)}
                        >
                          Yasal Bildirimi Görüntüle
                        </Menu.Item>
                        <Menu.Item
                          key="sendFinancialMail"
                          onClick={() => openManualEmailModal(record, 'FINANCIAL')}
                        >
                          Mali Rapor Gönder
                        </Menu.Item>
                        <Menu.Item
                          key="sendReportMail"
                          onClick={() => openManualEmailModal(record, 'REPORT')}
                        >
                          Kontrol-Rapor Maili Gönder
                        </Menu.Item>

                        <Popconfirm placement="left"
                          title=" Rapor ve faturayı silmek istediğinize emin misiniz?"
                          onConfirm={() => handleApprove(record.id, 5)}
                          okText="Evet"
                          cancelText="Hayır"
                        >
                          <Menu.Item key="revertInvoice">
                            Rapor ve Faturayı İptal Et
                          </Menu.Item>
                        </Popconfirm>
                        <Menu.Item
                          key="reportView"
                          disabled={
                            record.financialInfoUrl == null ||
                            record.invoiceAmount == null ||
                            record.reportUrl == null
                          }
                          onClick={() => handleOpenInvoiceModal(record, 'view')}
                        >
                          Rapor ve Faturayı Görüntüle
                        </Menu.Item>
                        {isControlCompletedProposal(record) && (
                          <Menu.Item
                            key="createFromCompletedControl"
                            onClick={() =>
                              navigate(buildOpportunityProposalCreateLink(record.id, 'upcoming-projects'))
                            }
                          >
                            Teklif Oluştur
                          </Menu.Item>
                        )}
                      </>
                    )}
                    {type === 7 && (
                      <>
                        <Menu.Item>
                          <ProposalView proposalId={record.id} proposalUrl={record.proposalUrl} />
                        </Menu.Item>
                        <Menu.Item
                          key="uploadModal"
                          onClick={() => openUploadModal(record.id, 2)}
                          disabled={!record.documentId}
                        >
                          Sözleşmeyi Görüntüle
                        </Menu.Item>

                        <Popconfirm placement="left"
                          title="Planlamayı iptal etmek istediğinize emin misiniz?"
                          onConfirm={() => handleApprove(record.id, 6)}
                          okText="Evet"
                          cancelText="Hayır"
                        >

                          {/* <Popconfirm
                        placement="left"
                        title="Yasal bildirim işlemini tamamlamak istediğinize emin misiniz?"
                        onConfirm={async () => {
                          const ok = await canCompleteLegalNotification(record);
                          if (!ok) return;
                          handleApprove(record.id, 5, 'Yasal bildirim işlemi tamamlandı.');
                        }}
                        okText="Evet"
                        cancelText="Hayır"
                      >
                        <Menu.Item key="completeLegal">
                          Yasal Bildirim İşlemini Tamamla
                        </Menu.Item>
                      </Popconfirm> */}
                          <Menu.Item onClick={() => navigateDetailPlan(record)}>
                            Planı Görüntüle
                          </Menu.Item>
                          <Menu.Item key="revertPlan">
                            Planı İptal Et
                          </Menu.Item>
                        </Popconfirm>
                        <Menu.Item
                          key="categoryList"
                          onClick={() => openCategoryDrawer(record)}
                        >
                          Yasal Bildirim Yap
                        </Menu.Item>
                        <Menu.Item
                          key="sendPlanningMail"
                          onClick={() => openManualEmailModal(record, 'PLAN')}
                        >
                          Planlama Maili Gönder
                        </Menu.Item>

                      </>
                    )}
                    {type === 3 && (
                      <>
                        <Menu.Item>
                          <ProposalView proposalId={record.id} proposalUrl={record.proposalUrl} />
                        </Menu.Item>
                        <Menu.Item
                          key="uploadModal"
                          onClick={() => openUploadModal(record.id, 2)}
                          disabled={!record.documentId}
                        >
                          Sözleşmeyi Görüntüle
                        </Menu.Item>
                        <Menu.Item onClick={() => navigateDetailPlan(record)}>
                          Planı Görüntüle
                        </Menu.Item>
                        <Menu.Item
                          key="categoryList"
                          onClick={() => openCategoryDrawer(record)}
                        >
                          Yasal Bildirimi Görüntüle
                        </Menu.Item>

                        <Popconfirm placement="left"
                          title="Yasal bildirimi iptal etmek istediğinize emin misiniz?"
                          onConfirm={() => handleApprove(record.id, 7)}
                          okText="Evet"
                          cancelText="Hayır"
                        >
                          <Menu.Item key="revertPlan">
                            Yasal Bildirimi İptal Et
                          </Menu.Item>
                        </Popconfirm>
                        <Menu.Item
                          key="upload"
                          onClick={async () => {
                            const ok = await canCompleteControl(record);
                            if (!ok) return;
                            handleOpenInvoiceModal(record, 'edit');
                          }}
                        >
                          Kontrolü Tamamla
                        </Menu.Item>


                      </>
                    )}
                    {type === 5 && (
                      <>
                        <Menu.Item
                          key="payment"
                          onClick={() => openPaymentModal(record.id, 'edit')}
                          disabled={record.paidAmount || !record?.customer?.identityDocumentConfirmAt}
                          style={(!record?.customer?.identityDocumentConfirmAt && !record.paidAmount) ? { pointerEvents: 'auto' } : undefined}
                        >
                          {(!record?.customer?.identityDocumentConfirmAt && !record.paidAmount) ? (
                            <Tooltip title="İş Ortağının Kimlik Kartının sistemde kimlik kartı olmaması sebebiyle ödeme yapılamamaktadır">
                              <span>Ödeme Yap</span>
                            </Tooltip>
                          ) : (
                            'Ödeme Yap'
                          )}
                        </Menu.Item>
                        <Menu.Item
                          key="upload"
                          disabled={record.paidAmount == null}
                          onClick={() => openPaymentModal(record.id, 'view')}
                        >
                          Ödeme Görüntüle
                        </Menu.Item>
                      </>
                    )}
                    {type === 2 && (
                      <>
                        <Menu.Item>
                          <ProposalView proposalId={record.id} proposalUrl={record.proposalUrl} />
                        </Menu.Item>
                        <Menu.Item
                          key="uploadModal"
                          onClick={() => openUploadModal(record.id, 2)}
                          disabled={!record.documentId}
                        >
                          Sözleşmeyi Görüntüle
                        </Menu.Item>
                        {isAdmin && (
                          <Popconfirm placement="left"
                            title="Onayı iptal etmek istediğinize emin misiniz?"
                            onConfirm={() => handleApprove(record.id, 10)}
                            okText="Evet"
                            cancelText="Hayır"
                          >
                            <Menu.Item key="revertApprove">
                              Sözleşme Onayını İptal Et
                            </Menu.Item>
                          </Popconfirm>
                        )}
                        {/* <Menu.Item onClick={() => openPlanningModal(record)}>
                          Planla
                        </Menu.Item> */}
                        <Menu.Item onClick={() => navigateDetailPlan(record)}>
                          Plan Yap
                        </Menu.Item>
                      </>
                    )}
                  </Menu>
                }
              >
                <Button type="default" className="d-button action-button">
                  İşlemler
                </Button>
              </Dropdown>
            </Row>
          )}
          {type === 5 && <>
            {
              <>
                {paymentType === 1 && (
                  <Tooltip
                    title={record?.customer?.identityDocumentConfirmAt ? undefined : 'İş Ortağının Kimlik Kartının sistemde kimlik kartı olmaması sebebiyle ödeme yapılamamaktadır'}
                  >
                    <span style={{ display: 'inline-block' }}>
                      <Button
                        type="default"
                        onClick={() => openPaymentModal(record.id, 'edit')}
                        className="d-button action-button"
                        disabled={!record?.customer?.identityDocumentConfirmAt}
                      >
                        Ödeme Yap
                      </Button>
                    </span>
                  </Tooltip>
                )}
                {paymentType === 2 && <Button type="default" href={record.financialInfoUrl} target="_blank" className="d-button action-button">
                  Ödeme Görüntüle
                </Button>}
              </>
            }
          </>}
        </>
    },

  ];

  if (isCompact) {
    let compactColumnKeys: string[] | null = null;

    if (type === 7) {
      compactColumnKeys = ['companyName', 'proposalNumber', 'createdAt', 'action'];
    } else if (type === 1) {
      compactColumnKeys = ['companyName', 'proposalNumber', 'createdAt', 'documentUploadedAt', 'action'];
    }

    if (compactColumnKeys) {
      columns = columns
        .filter((column) => column?.key && compactColumnKeys!.includes(column.key))
        .map((column) =>
          column.key === 'action'
            ? { ...column, title: 'İşlemler' }
            : column
        );
    }
  }

  const canPlan = async (record: any) => {
    try {
      if (!record) return true;
      const hasPlanContact = hasMatchingAuthorizedRecipients(record, 'PLAN');
      if (!hasPlanContact) {
        openNotification('warning', 'Uyarı', 'Teklif adresi ile eşleşen ve PLAN iletişim konusu seçili yetkili kişi bulunmamaktadır.');
        return false;
      }
      return true;
    } catch (e) {
      // Servis hatasında engelleme
      return true;
    }
  };

  const openPlanningModal = async (record: any) => {
    const ok = await canPlan(record);
    if (!ok) return;
    setSelectedProposalId(record.id);
    setIsPlanningModalVisible(true);
  };

  const navigateDetailPlan = async (record: any) => {
    const ok = await canPlan(record);
    if (!ok) return;
    navigate(`/planning/detail/${record.id}`);
  };

  const openUploadModal = (proposalId: number, type: number) => {
    setSelectedProposalId(proposalId);
    setIsUploadModalVisible(true);
    setDocumentModalType(type);
  };

  const openDocumentModal = (proposalId: number) => {
    setSelectedProposalId(proposalId);
    setIsDocumentModalVisible(true);
  };

  const canCompleteControl = async (record: any) => {
    try {
      if (!record) return true;
      const hasReport = hasMatchingAuthorizedRecipients(record, 'REPORT');
      const hasInvoice = hasMatchingAuthorizedRecipients(record, 'INVOICE');
      if (!hasReport || !hasInvoice) {
        openNotification('warning', 'Uyarı', 'Teklif adresi ile eşleşen RAPOR ve/veya FATURA iletişim konusu seçili yetkili kişi bulunmamaktadır.');
        return false;
      }
      return true;
    } catch (e) {
      return true;
    }
  };

  async function canCompleteLegalNotification(record: any) {
    try {
      if (!record) return true;
      const hasLegal = hasMatchingAuthorizedRecipients(record, 'LEGAL');
      if (!hasLegal) {
        openNotification('warning', 'Uyarı', 'Teklif adresi ile eşleşen ve YASAL İŞLEMLER iletişim konusu seçili yetkili kişi bulunmamaktadır.');
        return false;
      }
      return true;
    } catch (e) {
      return true;
    }
  }

  // Drawer: Yetkili kişiler
  const renderAuthorizedDrawer = () => (
    <Drawer
      title="Yetkili Kişiler"
      open={isAuthorizedDrawerVisible}
      onClose={() => setIsAuthorizedDrawerVisible(false)}
      width={1000}
      destroyOnClose
      footer={
        <div style={{ textAlign: 'right' }}>
          <Button style={{ marginRight: 8 }} onClick={() => setIsAuthorizedDrawerVisible(false)}>
            İptal
          </Button>
          <Button
            type="primary"
            className="d-button dark-button width-auto"
            onClick={handleSaveAuthorizedPersons}
            disabled={!selectedAuthorizedIds || selectedAuthorizedIds.length === 0}
          >
            Kaydet
          </Button>
        </div>
      }
    >
      {authorizedCompanyId && (
        <AuthorizedPersonTable
          companyId={authorizedCompanyId}
          showSelection={true}
          selectionFilter={'hasAny'}
          showSelectionMessage={false}
          selectedAuthorizedPersonIds={selectedAuthorizedIds}
          setSelectedAuthorizedPersonIds={setSelectedAuthorizedIds}
        />
      )}
    </Drawer>
  );

  useEffect(() => {
    if (isStatusChange) {
      handleApprove(selectedProposalId, 5);
      setIsStatusChange(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStatusChange]);

  const handleUpdateRequest = async (data: {
    invoiceAmount: string;
    reportUrl: string;
    financialInfoUrl: string;
    invoiceDate: any;
    invoiceEmailSendAt?: string;
  }) => {
    if (!selectedProposalId) {
      console.error('Proposal ID is not set');
      openNotification('error', 'Hata', "Teklif ID'si bulunamadı.");
      return;
    }

    try {
      const payload = {
        invoiceAmount: data.invoiceAmount,
        reportUrl: data.reportUrl,
        financialInfoUrl: data.financialInfoUrl,
        invoiceDate: data.invoiceDate,
        invoiceEmailSendAt: data.invoiceEmailSendAt,
      };

      await ProposalService.update(selectedProposalId, payload);
      openNotification('success', 'Başarılı', 'Rapor ve mali bilgiler eklendi.');
      setIsInvoiceModalVisible(false);
      fetchProposals();
    } catch (error) {
      console.error('Error updating proposal:', error);
      openNotification('error', 'Hata', 'Değişiklik kaydedilemedi.');
    }
  };

  const handleOpenInvoiceModal = (proposal: any, mode: 'edit' | 'view') => {
    if (!proposal.id) {
      console.error('Proposal ID is missing');
      openNotification('error', 'Hata', "Teklif ID'si bulunamadı.");
      return;
    }

    const shouldResetInvoiceEmailSendAt = type === 3 && mode === 'edit';

    setSelectedProposalId(proposal.id);
    setInvoiceModalData({
      invoiceAmount: proposal.invoiceAmount || '',
      reportUrl: proposal.reportUrl || '',
      financialInfoUrl: proposal.financialInfoUrl || '',
      invoiceDate: proposal.invoiceDate || '',
      invoiceEmailSendAt: shouldResetInvoiceEmailSendAt ? null : proposal.invoiceEmailSendAt || null,
    });

    setIsInvoiceModalVisible(true);
    setInvoiceModalType(mode);
  };

  const showHeader = !isCompact;

  const paginationConfig: any = {
    current: currentPage,
    pageSize,
    total: totalItems,
    onChange: (page: number, newPageSize?: number) => {
      setCurrentPage(page);
      setPageSize(newPageSize || initialPageSize);
    },
    showSizeChanger: !isCompact,
    defaultPageSize: initialPageSize,
  };

  if (!isCompact) {
    paginationConfig.pageSizeOptions = ['50', '100', '200', '500'];
  }

  const formatSummaryDays = (minutes: number) => {
    if (minutes <= 0) {
      return '0 (0.00)';
    }

    return `${Math.ceil(minutes / 480)} (${(minutes / 480).toFixed(2)})`;
  };

  const summaryTotalsContent = shouldShowSummaryTotals ? (
    <AntSpace
      size={[8, 4]}
      wrap
      style={{
        alignItems: 'center',
        fontSize: 12,
        maxWidth: 620,
        opacity: summaryLoading ? 0.65 : 1,
      }}
    >
      {isAdmin && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          Toplam Teklif Tutarı: <strong>{formatToTL(summaryTotals.totalPrice)}</strong>
        </Text>
      )}
      {canViewEffortSummaryTotals && (
        <>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Makine: <strong>{formatSummaryDays(summaryTotals.machineMinutes)}</strong>
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Elektrik: <strong>{formatSummaryDays(summaryTotals.electricMinutes)}</strong>
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            İş Hijyeni: <strong>{formatSummaryDays(summaryTotals.hygieneMinutes)}</strong>
          </Text>
        </>
      )}
      {summaryLoading && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          Güncelleniyor...
        </Text>
      )}
    </AntSpace>
  ) : null;

  const headerContent = showHeader ? (
    <Row justify="space-between" align="middle">
      <Col>
        <Row align="bottom">
          <Title level={4} style={{ margin: 0 }}>
            {type === 1 && <>Sözleşme Listesi</>}
            {type === 2 && <>Planlama Listesi</>}
            {type === 7 && <>Yasal Bildirim (İSG-Katip) Listesi</>}
            {type === 3 && <>Kontrol ve Raporlama Listesi</>}
            {type === 4 && <>Tamamlanan Projeler</>}
            {(type === 5 && paymentType === 1) && <>Ödemesi Planlanmış Projeler</>}
            {(type === 5 && paymentType === 2) && <>Ödemesi Tamamlanan Projeler</>}
          </Title>
          <Text type="secondary" className="ms-5" ><strong>({totalItems})</strong> kayıt listelendi</Text>
        </Row>
      </Col>
      <Col>
        <Row align="middle">
          <Col>
            <AntSpace>
              {summaryTotalsContent}
              <Input
                allowClear
                placeholder="Ara"
                prefix={<SearchOutlined style={{ color: '#b8b9bc' }} />}
                className="d-input d-input-filter"
                style={{ width: 200 }}
                value={inputValue}
                onChange={handleInputChange}
              />
              <Tooltip title="Dışa Aktar">
                <Button
                  shape='circle'
                  className='excel-icon-button'
                  onClick={handleExportExcel}
                  aria-label='Excel indir'
                >
                  <FileExcelOutlined />
                </Button>
              </Tooltip>
              <Button type="primary" onClick={showDrawer}>
                Filtrele
              </Button>
            </AntSpace>
          </Col>
        </Row>
      </Col>
    </Row>
  ) : null;

  const tableContent = (
    <Table
      style={{ marginTop: showHeader ? 24 : 0 }}
      loading={loading}
      scroll={{ x: 768 }}
      columns={columns}
      dataSource={filteredProposals}
      rowKey="proposalNumber"
      pagination={paginationConfig}
      onChange={handleTableChange}
    />
  );

  const modalContent = (
    <>
      {renderAuthorizedDrawer()}

      {isPlanningModalVisible && selectedProposalId !== null && (
        <PlanningModal
          proposalId={selectedProposalId}
          isModalVisible={isPlanningModalVisible}
          setIsModalVisible={setIsPlanningModalVisible}
          setIsStatusChange={setIsStatusChange}
        />
      )}

      {isUploadModalVisible && selectedProposalId !== null && (
        <DocumentUploadModal
          setIsModalVisible={setIsUploadModalVisible}
          isModalVisible={isUploadModalVisible}
          proposalId={selectedProposalId}
          isCustomer={true}
          type={documentModalType}
        />
      )}

      {isDocumentModalVisible && selectedProposalId !== null && (
        <ApproveDocument
          setIsModalVisible={setIsDocumentModalVisible}
          isModalVisible={isDocumentModalVisible}
          proposalId={selectedProposalId}
          isCustomer={true}
          type={2}
        />
      )}

      {isInvoiceModalVisible && selectedProposalId && (
        <ReportInvoiceModal
          visible={isInvoiceModalVisible}
          onClose={handleCloseInvoiceModal}
          onSubmit={handleUpdateRequest}
          initialValues={invoiceModalData}
          type={invoiceModalType}
        />
      )}

      {isPaymentModalVisible && selectedProposalId && (
        <PaymentModal
          visible={isPaymentModalVisible}
          onClose={() => setIsPaymentModalVisible(false)}
          proposalId={selectedProposalId}
          mode={paymentModalType}
        />
      )}

      <NegativeProposalReasonModal
        open={negativeModalVisible}
        onCancel={closeNegativeProposalReasonModal}
        onSubmit={handleNegativeProposalSubmit}
        confirmLoading={negativeModalLoading}
      />

      <ProposalProcessHistoryDrawer
        open={isNotesDrawerOpen}
        proposal={noteTargetProposal}
        isAdmin={isAdmin}
        statuses={statuses}
        titleMode="follow-up"
        onClose={handleCloseNotesDrawer}
      />

      <CategoryListDrawer
        open={isCategoryDrawerVisible}
        proposalId={categoryDrawerProposalId}
        onClose={closeCategoryDrawer}
        readOnly={categoryDrawerReadOnly}
      />

      <ReportApprovalDrawer
        open={isReportApprovalDrawerVisible}
        proposalId={reportApprovalProposalId}
        proposalNumber={reportApprovalProposalNumber}
        onClose={closeReportApprovalDrawer}
        onSummaryChange={handleReportApprovalSummaryChange}
      />

      <Modal
        title="Sorumlu Güncelle"
        open={assigneeModalVisible}
        onCancel={handleCloseAssigneeModal}
        onOk={handleAssigneeUpdate}
        confirmLoading={assigneeUpdateLoading}
        okText="Kaydet"
        cancelText="İptal"
        destroyOnClose
      >
        <Form layout="vertical" form={assigneeForm}>
          {assigneeSettings.map((setting) => {
            const fieldName = ASSIGNEE_SETTING_FIELD_MAP[setting.key as typeof ASSIGNEE_SETTING_KEYS[number]];
            if (!fieldName) return null;
            const label = setting.title || ASSIGNEE_SETTING_TITLE_MAP[setting.key] || setting.key;
            return (
              <Form.Item key={setting.key} name={fieldName} label={label}>
                <Select
                  allowClear
                  showSearch
                  placeholder={`${label} seçiniz`}
                  optionFilterProp="children"
                  loading={assigneeModalLoading}
                  filterOption={(input, option) => {
                    const child = option?.children as any;
                    const labelText =
                      typeof child === 'string'
                        ? child
                        : child && typeof child.toString === 'function'
                          ? child.toString()
                          : '';
                    return labelText.toLowerCase().includes(input.toLowerCase());
                  }}
                >
                  {(() => {
                    const currentValue = assigneeForm.getFieldValue(fieldName);
                    const currentId = Number(currentValue);
                    const options = [...assignableUsers];
                    if (!Number.isNaN(currentId) && currentId > 0) {
                      const exists = options.some((user) => Number(user?.id) === currentId);
                      if (!exists) {
                        const fallbackUser =
                          assignableUsersAll.find((user) => Number(user?.id) === currentId) ||
                          {
                            id: currentId,
                            firstName: 'Kullanıcı',
                            lastName: `#${currentId}`,
                          };
                        options.push(fallbackUser);
                      }
                    }
                    return options.map((user) => {
                      const optionId = Number(user?.id);
                      if (Number.isNaN(optionId) || optionId <= 0) {
                        return null;
                      }
                      const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
                      const labelText = fullName || user.email || user.id;
                      return (
                        <Select.Option key={optionId} value={String(optionId)}>
                          {labelText}
                        </Select.Option>
                      );
                    });
                  })()}
                </Select>
              </Form.Item>
            );
          })}
          {assigneeSettings.length === 0 && (
            <Text type="secondary">Sorumlu ayarları yüklenemedi.</Text>
          )}
        </Form>
      </Modal>

      <Modal
        title="Vade Günü Düzenle"
        open={isDueDayModalVisible}
        onCancel={handleCloseDueDayModal}
        onOk={handleDueDayUpdate}
        confirmLoading={dueDayUpdateLoading}
        okText="Kaydet"
        cancelText="İptal"
        destroyOnClose
      >
        <Form layout="vertical" form={dueDayForm}>
          <Form.Item
            label="Vade Günü"
            name="dueDay"
            rules={[
              { required: true, message: 'Vade günü giriniz' },
              { type: 'number', min: 1, message: 'Vade günü 1 veya daha büyük olmalıdır' },
            ]}
          >
            <InputNumber style={{ width: '100%' }} min={1} />
          </Form.Item>
        </Form>
      </Modal>

      <RenewalSettingsDrawer
        open={renewalSettingsOpen}
        proposal={renewalSettingsTargetProposal}
        loading={renewalSettingsLoading}
        onClose={closeRenewalSettingsDrawer}
        onSubmit={handleRenewalSettingsSubmit}
      />

      <Modal
        title={getManualEmailModalTitle(manualEmailType)}
        open={manualEmailModalVisible}
        onCancel={closeManualEmailModal}
        onOk={handleManualEmailSend}
        confirmLoading={manualEmailModalLoading}
        okButtonProps={{ disabled: manualEmailRecipientOptions.length === 0 }}
        okText="Gönder"
        cancelText="İptal"
        destroyOnClose
      >
        <Form layout="vertical" form={manualEmailForm}>
          <Form.Item
            label="E-posta Adresleri"
            name="emails"
            rules={[
              { required: true, message: 'En az bir e-posta adresi seçiniz' },
            ]}
          >
            <Select
              mode="multiple"
              placeholder="Gönderilecek e-posta adreslerini seçiniz"
              options={manualEmailRecipientOptions}
              optionFilterProp="label"
              showSearch
            />
          </Form.Item>
          {manualEmailRecipientOptions.length === 0 ? (
            <Text type="secondary">{getManualEmailRecipientWarning(manualEmailType)}</Text>
          ) : (
            <Text type="secondary">
              Yalnızca teklif adresi ile eşleşen uygun yetkili kişilerin e-posta adresleri listelenir.
            </Text>
          )}
          {(manualEmailType === 'REPORT' || manualEmailType === 'FINANCIAL') && (
            <p>
              <Text type="secondary">
                E-posta başarıyla gönderildikten sonra seçilen yetkili kişilerin kayıtlı cep telefonu
                numaralarına SMS bilgilendirmesi gönderilir.
              </Text>
            </p>
          )}
        </Form>
      </Modal>

      <DiscountModal
        visible={isDiscountModalVisible}
        totalPrice={
          selectedProposal
            ? selectedProposal.originalPrice ?? selectedProposal.totalPrice ?? null
            : null
        }
        existingDiscountAmount={selectedProposal?.discountedPrice ?? null}
        onCancel={handleModalCancel}
        onSubmit={handleDiscountSubmit}
        disableSubmit={Boolean(selectedProposal?.isTotalPriceSet)}
        submitLoading={isDiscountSubmitLoading}
        maxDiscountRate={currentUserMaxDiscountRate}
      />
      {discountPdfArchiveProposalId && (
        <PdfUpload
          key={`discount-pdf-archive-${discountPdfArchiveProposalId}`}
          proposalId={discountPdfArchiveProposalId}
          isCreate={false}
          silent
          shouldNavigate={false}
          onSuccess={handleDiscountPdfArchiveSuccess}
          onError={handleDiscountPdfArchiveError}
        />
      )}
    </>
  );


  const filterDrawer = (
    <Drawer
      title="Filtre"
      closable={{ 'aria-label': 'Close Button' }}
      onClose={onClose}
      open={open}
    >
      <Row gutter={[16, 16]}>
        <Col span={24}>
          {(isAdmin || isCentralUser) && (
            <Checkbox
              checked={onlyMyCompanies}
              onChange={(e) => setOnlyMyCompanies(e.target.checked)}
              style={{ marginLeft: 16 }}
            >
              Sadece kendi tekliflerimi listele
            </Checkbox>
          )}
        </Col>

        {(isAdmin || isCentralUser) && (
          <>
            <Col span={24}>
              <Select
                showSearch
                allowClear
                placeholder="Kullanıcı"
                style={{ width: '100%' }}
                value={selectedCustomerId}
                onChange={handleCustomerChange}
                optionFilterProp="label"
                filterOption={(input, option: any) =>
                  normalizeTurkish(option?.label).includes(normalizeTurkish(input))
                }
              >
                {customerList.map((customer: any) => {
                  const label = `${customer?.firstName} ${customer?.lastName} - ${customer?.email}`;
                  return (
                    <Select.Option key={customer.id} value={customer.id} label={label}>
                      {label}
                    </Select.Option>
                  );
                })}
              </Select>
            </Col>

            <Col span={24}>
              <Select
                showSearch
                allowClear
                placeholder="Firma"
                style={{ width: '100%' }}
                value={selectedCompanyId}
                onChange={handleCompanyChange}
                optionFilterProp="label"
                filterOption={(input, option: any) =>
                  normalizeTurkish(option?.label).includes(normalizeTurkish(input))
                }
              >
                {companyList.map((company: any) => (
                  <Select.Option
                    key={company.id}
                    value={company.id}
                    label={company.name}
                  >
                    {company.name}
                  </Select.Option>
                ))}
              </Select>
            </Col>
          </>
        )}

        {(type !== 5) && <Col span={24}>
          <Select
            showSearch
            allowClear
            placeholder="İl"
            style={{ width: '100%' }}
            value={selectedCityId}
            onChange={handleCityChange}
            optionFilterProp="label"
            filterOption={(input, option: any) =>
              normalizeTurkish(option?.label).includes(normalizeTurkish(input))
            }
          >
            {cities
              ?.filter((city) => cityIdsToShow.includes(city.id))
              .map((city: any) => (
                <Select.Option key={city.id} value={city.id} label={city.name}>
                  {city.name}
                </Select.Option>
              ))}
          </Select>
        </Col>
        }

        {(type !== 5) &&
          <Col span={24}>
            <Select
              showSearch
              allowClear
              placeholder="İlçe"
              style={{ width: '100%' }}
              value={selectedDistrictId}
              onChange={handleDistrictChange}
              optionFilterProp="label"
              filterOption={(input, option: any) =>
                normalizeTurkish(option?.label).includes(normalizeTurkish(input))
              }
            >
              {districts.map((district: any) => (
                <Select.Option key={district.id} value={district.id} label={district.name}>
                  {district.name}
                </Select.Option>
              ))}
            </Select>
          </Col>
        }


        {(type === 4) && (
          <Col span={24}>
            <label>Fatura Tarihi</label>
            <RangePicker
              format="DD/MM/YYYY"
              locale={locale}
              className="d-input-filter mr-8px"
              value={[
                invoiceDateRange[0] ? dayjs(invoiceDateRange[0]) : null,
                invoiceDateRange[1] ? dayjs(invoiceDateRange[1]) : null,
              ]}
              onChange={onInvoiceDateRangeChange}
            />
          </Col>
        )}


        {(type === 3 || type === 4 || type === 7) && (
          <Col span={24}>
            <label>Kontrol Tarihi</label>
            <RangePicker
              format="DD/MM/YYYY"
              locale={locale}
              className="d-input-filter mr-8px"
              value={[
                approvedPlanningDateRange[0] ? dayjs(approvedPlanningDateRange[0]) : null,
                approvedPlanningDateRange[1] ? dayjs(approvedPlanningDateRange[1]) : null,
              ]}
              onChange={onApprovedPlanningDateRangeChange}
            />
          </Col>
        )}


        {(type === 1 || type === 4) && (
          <Col span={24}>
            <label>Sözleşme Yükleme Tarihi</label>
            <RangePicker
              format="DD/MM/YYYY"
              locale={locale}
              className="d-input-filter mr-8px"
              value={[
                documentUploadedAtRange[0] ? dayjs(documentUploadedAtRange[0]) : null,
                documentUploadedAtRange[1] ? dayjs(documentUploadedAtRange[1]) : null,
              ]}
              onChange={onDocumentUploadedAtRangeChange}
            />
          </Col>
        )}

        {type === 2 && (
          <Col span={24}>
            <label>Planlama Talep Tarihi</label>
            <RangePicker
              format="DD/MM/YYYY"
              locale={locale}
              className="d-input-filter mr-8px"
              value={[
                requestedPlanningDateRange[0] ? dayjs(requestedPlanningDateRange[0]) : null,
                requestedPlanningDateRange[1] ? dayjs(requestedPlanningDateRange[1]) : null,
              ]}
              onChange={onRequestedPlanningDateRangeChange}
            />
          </Col>
        )}

        {type !== 5 && (
          <Col span={24}>
            <label>Teklif Tarihi</label>
            <RangePicker
              format="DD/MM/YYYY"
              locale={locale}
              className="d-input-filter mr-8px d-input-filter"
              style={{ marginRight: 10 }}
              value={[
                dateRange[0] ? dayjs(dateRange[0]) : null,
                dateRange[1] ? dayjs(dateRange[1]) : null,
              ]}
              onChange={onDateRangeChange}
            />
          </Col>
        )}

        {type === 5 && paymentType === 2 && (
          <Col span={24}>
            <label>Ödeme Tarihi</label>
            <RangePicker
              format="DD/MM/YYYY"
              locale={locale}
              className="d-input-filter mr-8px"
              value={[
                paidDateRange[0] ? dayjs(paidDateRange[0]) : null,
                paidDateRange[1] ? dayjs(paidDateRange[1]) : null,
              ]}
              onChange={onPaidDateRangeChange}
            />
          </Col>
        )}

        {type === 5 && (
          <>
            <Col span={24}>
              <label>Ödeme Planı</label>
              <RangePicker
                format="DD/MM/YYYY"
                locale={locale}
                className="d-input-filter mr-8px"
                value={[
                  planningPaidDateRange[0] ? dayjs(planningPaidDateRange[0]) : null,
                  planningPaidDateRange[1] ? dayjs(planningPaidDateRange[1]) : null,
                ]}
                onChange={onPlanningPaidDateRangeChange}
              />
            </Col>
            <Col span={24}>
              <label>Fatura Tarihi</label>
              <RangePicker
                format="DD/MM/YYYY"
                locale={locale}
                className="d-input-filter mr-8px"
                value={[
                  invoiceDateRange[0] ? dayjs(invoiceDateRange[0]) : null,
                  invoiceDateRange[1] ? dayjs(invoiceDateRange[1]) : null,
                ]}
                onChange={onInvoiceDateRangeChange}
              />
            </Col>
            <Col span={24}>
              <label>Teklif Tarihi</label>
              <RangePicker
                format="DD/MM/YYYY"
                locale={locale}
                className="d-input-filter mr-8px d-input-filter"
                style={{ marginRight: 10 }}
                value={[
                  dateRange[0] ? dayjs(dateRange[0]) : null,
                  dateRange[1] ? dayjs(dateRange[1]) : null,
                ]}
                onChange={onDateRangeChange}
              />
            </Col>
          </>
        )}


      </Row>
      <Row gutter={[16, 16]} className="justify-content-end mt-16px">
        <Col>
          <Button
            type="default"
            onClick={() => filterClear()}
          >
            Temizle
          </Button>
        </Col>
        <Col>
          <Button type="primary" onClick={() => getFilterProposal()}>
            Filtrele
          </Button>
        </Col>
      </Row>
    </Drawer>
  );

  if (isCompact) {
    return (
      <>
        {headerContent}
        {tableContent}
        {modalContent}
        {filterDrawer}
      </>
    );
  }

  return (
    <Card className="br-30px mb-60px proposal-card">
      {headerContent}
      {tableContent}
      {modalContent}
      {filterDrawer}
    </Card>
  );
};

export default ProposalListWithDetails;
